import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { subsampleStitches } from "../SvgEditor/components/stitchParser";
import {
  findNearestPointAcrossSubpaths,
  splitAtIndices,
  validateSplitDistance,
  findNearestPoint as findNearestPointHelper,
  cutShapeAtIndices,
  pointsToClosedD,
} from "../SvgEditor/helpers/splitPathToRails";
import { computeSatinStitchD } from "./stitchGeometry";
import {
  parseHex,
  rgbToHls,
  hlsToRgb,
  darkerHex,
  lightenHex,
  threadBaseColor,
  buildStitchElements,
} from "./stitchRender";
import StitchEraseCanvas from "./StitchEraseCanvas";
import StitchFillCanvas from "./StitchFillCanvas";
import StitchPixelEraseCanvas from "./StitchPixelEraseCanvas";
import StitchDrawCanvas from "./StitchDrawCanvas";
import StitchMorphCanvas from "./StitchMorphCanvas";
import { getSatinColumnColor } from "./satinPalette";
import { railNodesToD } from "./railPathEditing";
import { resolveReferenceArtworkRegistration } from "../SvgEditor/helpers/referenceArtworkRegistration";

const RENDER_SCALE = 4;

function splitCompoundD(d) {
  if (!d) return [];
  return d.split(/(?=M)/i).filter((s) => s.trim());
}


function samplePathPoints(d, numSamples) {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.style.position = "absolute";
  svg.style.width = "0";
  svg.style.height = "0";
  svg.style.overflow = "hidden";
  const path = document.createElementNS(ns, "path");
  path.setAttribute("d", d);
  svg.appendChild(path);
  document.body.appendChild(svg);
  const totalLen = path.getTotalLength();
  const pts = [];
  for (let i = 0; i <= numSamples; i++) {
    const pt = path.getPointAtLength((i / numSamples) * totalLen);
    pts.push({ x: pt.x, y: pt.y });
  }
  document.body.removeChild(svg);
  return pts;
}


const StitchCanvas = ({
  svgJoined, svgSplit, stitchSettings, targetWidth, targetHeight, hoopWidth, hoopHeight,
  realStitchData, stitchToViewbox,
  isConverting, convertStatus, onCancelConvert,
  activeRailDrawing, layerRails, currentLayerIdx,
  onAddRailPoint, onUpdateLastRailPoint, onRemoveRailPoint, onFinishRail, onCancelRail,
  railEditing, onStartRailEditing, onSelectRailNodes, onPreviewRailEdit, onCommitRailEdit, onInsertRailNode, onSelectRailNode, onDeleteRailNode,
  railSelection, onSelectPathAsRail, onCancelRailSelection,
  activeRungDrawing, onAddRungPoint,
  colors,
  embroidery, layerStitchSettings, aiAnalysis,
  aiLoading, layerPieces, autoDetectMeta, onDragSplitPoint,
  currentRailPairs, onStartRungDrawing, onClearRungs, onRemoveRailPair,
  eraseActive, eraseLayerIdx,
  cutToolRef,
  layerCutMarks,
  onEraseComplete, onEraseCancel,
  onCutMarksChange,
  onEraseDraftHistoryChange,
  fillActive, fillLayerIdx,
  onFillApply,
  fillToolRef,
  pixelEraseActive, pixelEraseLayerIdx,
  onPixelEraseApply,
  pixelEraseToolRef,
  drawActive, drawLayerIdx,
  onDrawApply,
  drawToolRef,
  morphActive, morphLayerIdx,
  onMorphApply,
  morphToolRef,
  layerSelected = "all",
  isPhone = false,
  smartCutActive = false,
  smartCutLayerIdx = null,
  onSmartCutSubpath,
  satinEditingActive = false,
  referenceArtwork = null,
  referenceArtworkVisible = true,
  referenceArtworkOpacity = 0.35,
  onToggleReferenceArtwork,
  onReferenceArtworkOpacityChange,
  onRetraceArtwork,
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const renderIdRef = useRef(0);
  const dragRef = useRef(null);
  // Keep a ref to embroidery so draw() doesn't need it as a dep (it's a new
  // object every render even when nothing changed, causing constant redraws).
  // Keep this current DURING render, not in an effect. getEffectiveLayerPasses
  // reads embroideryRef.current synchronously while computing vectorStitchElements,
  // and that memo recomputes the instant layerRails/colors change (e.g. on a layer
  // reorder). If the ref only updated in a post-render effect, the satin/fill
  // lookup would read the previous render's (pre-reorder) per-layer settings and
  // render satin on the wrong layer.
  const embroideryRef = useRef(embroidery);
  embroideryRef.current = embroidery;
  const [isDragging, setIsDragging] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [artworkControlsOpen, setArtworkControlsOpen] = useState(false);
  const panRef = useRef({ x: 0, y: 0 });
  const zoomRef = useRef(1);

  // ── Direct-DOM transform sync (bypasses React re-renders during gestures) ──
  const transformGroupRef = useRef(null);
  const gestureActiveRef = useRef(false);
  const rafIdRef = useRef(0);

  const syncTransformsToDOM = useCallback(() => {
    const p = panRef.current;
    const z = zoomRef.current;
    const g = transformGroupRef.current;
    if (g) g.setAttribute("transform", `translate(${p.x}, ${p.y}) scale(${z})`);
    const container = containerRef.current;
    if (!container) return;
    const overlays = container.querySelectorAll("[data-pan-zoom]");
    for (let i = 0; i < overlays.length; i++) {
      const el = overlays[i];
      const role = el.dataset.panZoom;
      if (role === "vector") {
        el.setAttribute("transform", `translate(${p.x}, ${p.y}) scale(${z})`);
      } else if (role === "design") {
        const dl = parseFloat(el.dataset.dl) || 0;
        const dt = parseFloat(el.dataset.dt) || 0;
        el.style.transform = `translate(${p.x + dl * z}px, ${p.y + dt * z}px) scale(${z})`;
      } else if (role === "canvas") {
        el.style.transform = `translate(${p.x}px, ${p.y}px) scale(${z})`;
      } else if (role === "point") {
        // Screen-sized controls follow the artwork without scaling the buttons.
        el.style.left = `${p.x + Number(el.dataset.canvasX) * z}px`;
        el.style.top = `${p.y + Number(el.dataset.canvasY) * z}px`;
      }
    }
  }, []);
  // Frozen on first real measurement — never updated on resize so the
  // canvas/SVG coordinate space stays stable (like the SVG editor).
  const [baseSize, setBaseSize] = useState({ w: 800, h: 600 });
  const baseMeasured = useRef(false);
  const [spacePressed, setSpacePressed] = useState(false);
  const spacePressedRef = useRef(false);
  const [elapsed, setElapsed] = useState(0);
  zoomRef.current = zoom;

  // A4: Sew-in animation — briefly animates when satin rails are first completed
  const [sewInActive, setSewInActive] = useState(false);
  const prevRailCountRef = useRef(0);
  useEffect(() => {
    const entry = layerRails?.[currentLayerIdx];
    const pairs = entry?.pairs || [];
    const completeCount = pairs.filter((p) => p.rail0 && p.rail1).length;
    if (completeCount > prevRailCountRef.current && prevRailCountRef.current >= 0) {
      setSewInActive(true);
      const t = setTimeout(() => setSewInActive(false), 500);
      // Haptic tick on mobile when rails snap into place
      if (navigator.vibrate) navigator.vibrate(12);
      return () => clearTimeout(t);
    }
    prevRailCountRef.current = completeCount;
  }, [layerRails, currentLayerIdx]);

  const isDrawingRail = !!activeRailDrawing;
  const isEditingRail = !!railEditing;
  const isSelectingRail = !!railSelection;
  const isDrawingRung = !!activeRungDrawing;
  const hasEditableLayer = typeof layerSelected === "number";
  const blocksViewportGestures = isDrawingRail || isEditingRail || isSelectingRail || isDrawingRung || eraseActive || fillActive || pixelEraseActive || drawActive || smartCutActive;
  const blocksViewportGesturesRef = useRef(false);
  const suppressNextDrawClickRef = useRef(false);
  const [hoveredPathIdx, setHoveredPathIdx] = useState(null);

  useEffect(() => {
    blocksViewportGesturesRef.current = blocksViewportGestures;
    if (!blocksViewportGestures) return;
    touchRef.current.active = false;
    dragRef.current = null;
    setIsDragging(false);
  }, [blocksViewportGestures]);

  useEffect(() => {
    if (!isConverting) { setElapsed(0); return; }
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [isConverting]);

  // Measure container size exactly once — freeze it so layout never jumps on window resize.
  useEffect(() => {
    if (baseMeasured.current) return;
    const el = containerRef.current;
    if (!el) return;
    const { offsetWidth: w, offsetHeight: h } = el;
    if (w > 0 && h > 0) {
      setBaseSize({ w: Math.max(400, w), h: Math.max(300, h) });
      baseMeasured.current = true;
    }
  }, []);

  // Zoom toward cursor (mouse wheel) or pan (trackpad two-finger scroll)
  // Mac pinch fires as ctrl+wheel — treat as zoom just like mouse wheel.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onWheel = (e) => {
      e.preventDefault();
      const isMacPinch = e.ctrlKey;
      // Trackpad: deltaMode 0 with small deltaY or any horizontal delta (no ctrl)
      const isTrackpad = !isMacPinch && e.deltaMode === 0 && (
        Math.abs(e.deltaY) < 50 || Math.abs(e.deltaX) > 0
      );

      if (isTrackpad) {
        const newPan = {
          x: panRef.current.x - e.deltaX,
          y: panRef.current.y - e.deltaY,
        };
        panRef.current = newPan;
        setPan(newPan);
      } else {
        const factor = e.deltaY < 0 ? 1.15 : 0.87;
        const rect = container.getBoundingClientRect();
        const cursorX = e.clientX - rect.left;
        const cursorY = e.clientY - rect.top;
        const prev = zoomRef.current;
        const next = Math.max(0.3, Math.min(20, prev * factor));
        const scale = next / prev;
        const newPan = {
          x: cursorX - scale * (cursorX - panRef.current.x),
          y: cursorY - scale * (cursorY - panRef.current.y),
        };
        zoomRef.current = next;
        panRef.current = newPan;
        setZoom(next);
        setPan(newPan);
      }
    };
    container.addEventListener("wheel", onWheel, { passive: false });
    return () => container.removeEventListener("wheel", onWheel);
  }, []);

  // Touch pinch-to-zoom and two-finger drag (mobile)
  const touchRef = useRef({ active: false, startDist: 0, startZoom: 1, startMid: { x: 0, y: 0 }, startPan: { x: 0, y: 0 } });
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const mid = (a, b) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 });

    const onTouchStart = (e) => {
      if (e.touches.length >= 2) {
        suppressNextDrawClickRef.current = true;
        e.preventDefault();
        const t = touchRef.current;
        t.active = true;
        t.startDist = dist(e.touches[0], e.touches[1]);
        t.startZoom = zoomRef.current;
        const m = mid(e.touches[0], e.touches[1]);
        t.startMid = m;
        t.startPan = { ...panRef.current };
        // Cancel any in-progress single-pointer drag
        dragRef.current = null;
        setIsDragging(false);
      }
    };

    const onTouchMove = (e) => {
      const t = touchRef.current;
      if (!t.active || e.touches.length < 2) return;
      e.preventDefault();

      const curDist = dist(e.touches[0], e.touches[1]);
      const curMid = mid(e.touches[0], e.touches[1]);
      const rect = container.getBoundingClientRect();

      // Zoom toward the midpoint between the two fingers
      const scale = curDist / t.startDist;
      const newZoom = Math.max(0.3, Math.min(20, t.startZoom * scale));
      const zoomRatio = newZoom / t.startZoom;
      const cx = t.startMid.x - rect.left;
      const cy = t.startMid.y - rect.top;

      // Pan = original pan adjusted for zoom change around the start midpoint,
      // plus the finger-midpoint drift for two-finger drag
      const panX = cx - zoomRatio * (cx - t.startPan.x) + (curMid.x - t.startMid.x);
      const panY = cy - zoomRatio * (cy - t.startPan.y) + (curMid.y - t.startMid.y);

      zoomRef.current = newZoom;
      panRef.current = { x: panX, y: panY };
      gestureActiveRef.current = true;
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(syncTransformsToDOM);
    };

    const onTouchEnd = (e) => {
      if (e.touches.length < 2) {
        touchRef.current.active = false;
        if (gestureActiveRef.current) {
          gestureActiveRef.current = false;
          setPan({ ...panRef.current });
          setZoom(zoomRef.current);
        }
      }
    };

    container.addEventListener("touchstart", onTouchStart, { passive: false });
    container.addEventListener("touchmove", onTouchMove, { passive: false });
    container.addEventListener("touchend", onTouchEnd);
    container.addEventListener("touchcancel", onTouchEnd);
    return () => {
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", onTouchEnd);
      container.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

  // Spacebar → temporary pan mode (like SVG editor)
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === "Space" && !spacePressedRef.current) {
        e.preventDefault();
        spacePressedRef.current = true;
        setSpacePressed(true);
      }
    };
    const onKeyUp = (e) => {
      if (e.code === "Space") {
        spacePressedRef.current = false;
        setSpacePressed(false);
        if (dragRef.current?.spacePan) {
          dragRef.current = null;
          setIsDragging(false);
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  const onPointerDown = useCallback((e) => {
    // Skip single-pointer drag while a multi-touch gesture is active
    if (touchRef.current.active) return;
    const isSpacePan = spacePressedRef.current;
    if (!isSpacePan && (isDrawingRail || isSelectingRail || isDrawingRung || eraseActive || fillActive || pixelEraseActive || drawActive || morphActive || smartCutActive)) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDragging(true);
    dragRef.current = { startX: e.clientX, startY: e.clientY, startPanX: panRef.current.x, startPanY: panRef.current.y, spacePan: isSpacePan };
  }, [isDrawingRail, isSelectingRail, isDrawingRung, eraseActive, fillActive, pixelEraseActive, drawActive, morphActive, smartCutActive]);

  const onPointerMove = useCallback((e) => {
    if (!dragRef.current || touchRef.current.active) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    const newPan = { x: dragRef.current.startPanX + dx, y: dragRef.current.startPanY + dy };
    panRef.current = newPan;
    gestureActiveRef.current = true;
    cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(syncTransformsToDOM);
  }, [syncTransformsToDOM]);

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
    setIsDragging(false);
    if (gestureActiveRef.current) {
      gestureActiveRef.current = false;
      setPan({ ...panRef.current });
      setZoom(zoomRef.current);
    }
  }, []);

  // Stable coordinate space — frozen at first measurement, never changes on resize.
  const BASE_W = baseSize.w;
  const BASE_H = baseSize.h;

  // Compute design layout metrics for coordinate mapping and SVG overlay
  const layoutMetrics = useMemo(() => {
    const W = BASE_W, H = BASE_H;
    const CX = W / 2;
    const topChrome = isPhone ? 110 : 80;
    const bottomChrome = isPhone ? 250 : 48;
    const CY = topChrome + Math.max(0, H - topChrome - bottomChrome) / 2;
    const PAD = 24;
    const hpW = hoopWidth || 100;
    const hpH = hoopHeight || 100;
    const fitW = W - PAD * 2;
    const fitH = H - topChrome - bottomChrome - PAD;
    const hoopScale = Math.min(fitW / hpW, fitH / hpH);
    const hoopPxW = hpW * hoopScale;
    const hoopPxH = hpH * hoopScale;

    const designMmW = targetWidth || hpW;
    const designMmH = targetHeight || hpH;

    // Parse SVG viewBox
    let vbX = 0, vbY = 0, vbW = 100, vbH = 100;
    if (svgJoined?.attributes?.viewBox) {
      const parts = svgJoined.attributes.viewBox.split(/\s+/).map(Number);
      if (parts.length === 4) { vbX = parts[0]; vbY = parts[1]; vbW = parts[2]; vbH = parts[3]; }
    }

    // Design pixels based on its mm size relative to the hoop
    const designPxW = (designMmW / hpW) * hoopPxW;
    const designPxH = (designMmH / hpH) * hoopPxH;
    const preScl = Math.min(designPxW / vbW, designPxH / vbH);
    let dw = vbW * preScl, dh = vbH * preScl;

    const designLeft = CX - dw / 2;
    const designTop = CY - dh / 2;
    const scaleX = dw / vbW;
    const scaleY = dh / vbH;

    return { CX, CY, hoopScale, hoopPxW, hoopPxH, vbX, vbY, vbW, vbH, designLeft, designTop, baseDesignLeft: designLeft, baseDesignTop: designTop, scaleX, scaleY, dw, dh, W, H };
  }, [BASE_W, BASE_H, targetWidth, targetHeight, hoopWidth, hoopHeight, svgJoined, isPhone]);

  // Crisp SVG layer elements — rendered in the design SVG overlay instead of rasterizing onto canvas.
  // Uses a compound path per layer so evenodd fill-rule correctly creates holes.
  const svgLayerElements = useMemo(() => {
    const source = svgSplit || svgJoined;
    if (!source?.children?.length) return null;

    const eraseIdx      = eraseActive      ? (eraseLayerIdx      ?? currentLayerIdx) : -1;
    const fillIdx       = fillActive       ? (fillLayerIdx       ?? currentLayerIdx) : -1;
    const pixelEraseIdx = pixelEraseActive ? (pixelEraseLayerIdx ?? currentLayerIdx) : -1;
    const drawIdx       = drawActive       ? (drawLayerIdx       ?? currentLayerIdx) : -1;
    const morphIdx      = morphActive      ? (morphLayerIdx      ?? currentLayerIdx) : -1;
    return source.children.map((layer, i) => {
      if (layerSelected !== "all" && i !== layerSelected) return null;
      const layerColor = colors?.[i] || "#888888";
      const attrs = layer?.attributes || {};
      const fillRule = attrs["fill-rule"] || attrs.fillRule || "evenodd";

      // Build a single compound d-string from the layer (handles both split
      // and joined formats). This preserves evenodd hole semantics.
      let compoundD = attrs.d || "";
      if (!compoundD && layer?.children?.length) {
        const parts = [];
        const collectD = (node) => {
          if (node?.attributes?.d) parts.push(node.attributes.d);
          if (node?.children) node.children.forEach(collectD);
        };
        layer.children.forEach(collectD);
        compoundD = parts.join(" ");
      }
      if (!compoundD) return null;

      return (
        <path
          key={i}
          d={compoundD}
          fill={layerColor}
          stroke={layerColor}
          strokeWidth="1"
          strokeLinejoin="miter"
          fillRule={fillRule}
          opacity={i === eraseIdx || i === fillIdx || i === pixelEraseIdx || i === drawIdx || i === morphIdx ? 0 : 1}
        />
      );
    });
  }, [svgSplit, svgJoined, colors, eraseActive, eraseLayerIdx, fillActive, fillLayerIdx, pixelEraseActive, pixelEraseLayerIdx, drawActive, drawLayerIdx, morphActive, morphLayerIdx, currentLayerIdx, layerSelected]);

  // Convert screen click coordinates to SVG viewBox coordinates.
  // Always uses the base layoutMetrics (viewBox-based) since rails are stored
  // in viewBox coords and the overlay always uses the viewBox coordinate system.
  const screenToSvgViewBox = useCallback((clientX, clientY) => {
    const container = containerRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    const canvasX = (clientX - rect.left - panRef.current.x) / zoomRef.current;
    const canvasY = (clientY - rect.top - panRef.current.y) / zoomRef.current;
    const { designLeft, designTop, scaleX, scaleY, vbX, vbY } = layoutMetrics;
    const svgX = (canvasX - designLeft) / scaleX + vbX;
    const svgY = (canvasY - designTop) / scaleY + vbY;
    return { x: svgX, y: svgY };
  }, [layoutMetrics]);

  const handleRailDblClick = useCallback((e) => {
    if (!isDrawingRail || !onFinishRail) return;
    e.preventDefault();
    onFinishRail();
  }, [isDrawingRail, onFinishRail]);

  // Keyboard: Enter to finish, Escape to cancel rail drawing
  useEffect(() => {
    if (!isDrawingRail) return;
    const handleKeyDown = (e) => {
      if (e.target?.closest?.("input, textarea, select, ion-input, ion-textarea, [contenteditable]")) return;
      if (e.key === "Enter") onFinishRail?.();
      if (e.key === "Escape") onCancelRail?.();
      if ((e.key === "Backspace" || e.key === "Delete") && activeRailDrawing?.points?.length) {
        e.preventDefault();
        onRemoveRailPoint?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDrawingRail, activeRailDrawing?.points?.length, onFinishRail, onCancelRail, onRemoveRailPoint]);

  // Keyboard: Escape to cancel rail selection
  useEffect(() => {
    if (!isSelectingRail) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onCancelRailSelection?.();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSelectingRail, onCancelRailSelection]);

  // Pre-sample the active pair's rails using SVG getPointAtLength so curved
  // (auto-detected) rails are followed accurately, not just their M/L vertices.
  // Re-runs only when the targeted pair (or its path strings) changes.
  const activePairSamplesRef = useRef({ r0: [], r1: [] });
  const activePair = activeRungDrawing && currentRailPairs
    ? currentRailPairs[activeRungDrawing.pairIdx]
    : null;
  const activeRail0 = activePair?.rail0;
  const activeRail1 = activePair?.rail1;
  useEffect(() => {
    if (!activeRail0 || !activeRail1) {
      activePairSamplesRef.current = { r0: [], r1: [] };
      return;
    }
    try {
      activePairSamplesRef.current = {
        r0: samplePathPoints(activeRail0, 200),
        r1: samplePathPoints(activeRail1, 200),
      };
    } catch (_) {
      activePairSamplesRef.current = { r0: [], r1: [] };
    }
  }, [activeRail0, activeRail1]);

  // Snap a viewBox point to the nearest point on either rail of the active pair.
  // Uses perpendicular projection between consecutive samples for sub-sample accuracy.
  const snapToActivePair = useCallback((pt) => {
    const { r0, r1 } = activePairSamplesRef.current;
    if (!r0.length || !r1.length) return pt;

    const nearestOnSegment = (p, a, b) => {
      const dx = b.x - a.x, dy = b.y - a.y;
      const len2 = dx * dx + dy * dy;
      if (len2 === 0) return { pt: a, dist: Math.hypot(p.x - a.x, p.y - a.y) };
      const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
      const nx = a.x + t * dx, ny = a.y + t * dy;
      return { pt: { x: nx, y: ny }, dist: Math.hypot(p.x - nx, p.y - ny) };
    };

    let best = null;
    let bestDist = Infinity;
    for (const pts of [r0, r1]) {
      for (let i = 0; i < pts.length; i++) {
        const vd = Math.hypot(pt.x - pts[i].x, pt.y - pts[i].y);
        if (vd < bestDist) { bestDist = vd; best = pts[i]; }
        if (i + 1 < pts.length) {
          const { pt: sp, dist: sd } = nearestOnSegment(pt, pts[i], pts[i + 1]);
          if (sd < bestDist) { bestDist = sd; best = sp; }
        }
      }
    }
    return best || pt;
  }, []);

  const handleRungClick = useCallback((e) => {
    if (!isDrawingRung || !onAddRungPoint) return;
    const pt = screenToSvgViewBox(e.clientX, e.clientY);
    if (pt) onAddRungPoint(snapToActivePair(pt).x, snapToActivePair(pt).y);
  }, [isDrawingRung, onAddRungPoint, screenToSvgViewBox, snapToActivePair]);

  const isSplitting = !!embroidery?.splitMode;

  const handleSplitClick = useCallback((e) => {
    if (!isSplitting) return;
    const pt = screenToSvgViewBox(e.clientX, e.clientY);
    if (pt) embroidery.setSplitPoint(pt.x, pt.y);
  }, [isSplitting, embroidery, screenToSvgViewBox]);

  const handleSplitMouseMove = useCallback((e) => {
    if (!isSplitting) return;
    const pt = screenToSvgViewBox(e.clientX, e.clientY);
    if (pt) embroidery.updateSplitHover(pt.x, pt.y);
  }, [isSplitting, embroidery, screenToSvgViewBox]);

  useEffect(() => {
    if (!isSplitting) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") embroidery?.cancelSplitMode();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSplitting, embroidery]);

  const isCutting = !!embroidery?.cutMode;

  const handleCutClick = useCallback((e) => {
    if (!isCutting) return;
    const pt = screenToSvgViewBox(e.clientX, e.clientY);
    if (pt) embroidery.setCutPoint(pt.x, pt.y);
  }, [isCutting, embroidery, screenToSvgViewBox]);

  const handleCutMouseMove = useCallback((e) => {
    if (!isCutting) return;
    const pt = screenToSvgViewBox(e.clientX, e.clientY);
    if (pt) embroidery.updateCutHover(pt.x, pt.y);
  }, [isCutting, embroidery, screenToSvgViewBox]);

  useEffect(() => {
    if (!isCutting) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") embroidery?.cancelCutMode();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isCutting, embroidery]);

  const draw = useCallback(async () => {
    const renderId = ++renderIdRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const curZoom = zoomRef.current;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    // Target: enough resolution so the stitch canvas is crisp at the current
    // zoom level on the device's physical pixels. Cap total pixel budget at
    // ~16 megapixels to avoid GPU memory issues on mobile.
    const MAX_PIXELS = 16_000_000;
    const W = BASE_W, H = BASE_H;
    const idealScale = Math.max(RENDER_SCALE, Math.ceil(curZoom * dpr));
    const pixelBudget = W * idealScale * H * idealScale;
    const effectiveScale = pixelBudget > MAX_PIXELS
      ? Math.floor(Math.sqrt(MAX_PIXELS / (W * H)))
      : idealScale;
    const newW = W * effectiveScale;
    const newH = H * effectiveScale;
    if (canvas.width !== newW || canvas.height !== newH) {
      canvas.width = newW;
      canvas.height = newH;
    }

    const ctx = canvas.getContext("2d");
    // Transparent canvas — the design SVG element below handles background/hoop/layer colors
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(effectiveScale, effectiveScale);

    // Skip stitch pattern rendering until AI analysis is ready
    if (aiLoading) {
      ctx.restore();
      return;
    }

    // Design rendering
    if (realStitchData && realStitchData.stitches.length > 0) {
      ctx.save();
      const { stitches, colors: parsedColors } = realStitchData;
      const threadColors = colors?.length
        ? parsedColors.map((_, i) => colors[i] || parsedColors[i])
        : parsedColors;
      const subsampleLimit = Math.min(200000, Math.round(50000 * Math.max(1, curZoom)));
      const display = subsampleStitches(stitches, subsampleLimit);

      // Map stitch file coords (0.1mm) → canvas pixels via SVG viewBox.
      //
      // Server transform: file_coord = (inkpx - origin) * (10/PIXELS_PER_MM)
      // Inverse: inkpx = file_coord / scale + origin
      //          mm    = inkpx / PIXELS_PER_MM = inkpx * scale / 10
      //          vb    = vbMin + (mm / size_mm) * vbSize
      //          canvas = (vb - vbMin) * layoutScl + designOffset
      const { hoopPxW, hoopPxH, CX, CY } = layoutMetrics;
      const { minX, maxX, minY, maxY } = realStitchData;
      const midStX = (minX + maxX) / 2;
      const midStY = (minY + maxY) / 2;
      const stW = maxX - minX;
      const stH = maxY - minY;
      let mapX, mapY, stitchScl;

      if (stW > 0 && stH > 0) {
        const pxPerUnit = Math.min(hoopPxW / stW, hoopPxH / stH) * 0.85;
        mapX = (v) => CX + (v - midStX) * pxPerUnit;
        mapY = (v) => CY + (v - midStY) * pxPerUnit;
        stitchScl = pxPerUnit;
      } else {
        mapX = (v) => CX + v * 0.1;
        mapY = (v) => CY + v * 0.1;
        stitchScl = 0.1;
      }

      if (window.__stitchDebugOnce !== true) {
        window.__stitchDebugOnce = true;
        const s0 = stitches[0];
        console.log("[StitchAlign] stitch bbox:", minX, minY, "→", maxX, maxY,
          "\n  center stitch:", midStX.toFixed(1), midStY.toFixed(1),
          "→ canvas:", mapX(midStX).toFixed(1), mapY(midStY).toFixed(1),
          "\n  canvas center:", CX, CY,
          "\n  first stitch:", s0?.x, s0?.y, "→ canvas:", mapX(s0?.x ?? 0).toFixed(1), mapY(s0?.y ?? 0).toFixed(1));
      }

      const segments = [];
      for (let i = 1; i < display.length; i++) {
        const p = display[i - 1], c = display[i];
        if (c.jump || c.colorIndex !== p.colorIndex) continue;
        segments.push({ x1: mapX(p.x), y1: mapY(p.y), x2: mapX(c.x), y2: mapY(c.y), ci: c.colorIndex });
      }

      const byColor = {};
      for (const seg of segments) (byColor[seg.ci] || (byColor[seg.ci] = [])).push(seg);

      const THREAD_MM = 0.4;
      const threadPx = THREAD_MM * 10 * stitchScl;
      const TW = Math.max(1.0, Math.min(4.5, threadPx));

      // Light direction for Blinn-Phong shading
      const lightAz = (145 * Math.PI) / 180;
      const lightEl = (55 * Math.PI) / 180;
      const lx = Math.cos(lightEl) * Math.cos(lightAz);
      const ly = Math.cos(lightEl) * Math.sin(lightAz);
      const lz = Math.sin(lightEl);
      const hx = lx, hy = ly, hz = lz + 1;
      const hLen = Math.sqrt(hx * hx + hy * hy + hz * hz);
      const hnx = hx / hLen, hny = hy / hLen, hnz = hz / hLen;

      ctx.lineCap = "butt";
      ctx.lineJoin = "miter";

      // Pre-compute per-segment shading
      for (const seg of segments) {
        const dx = seg.x2 - seg.x1, dy = seg.y2 - seg.y1;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len < 0.01) { seg.spec = 0; seg.nDotL = 0.3; continue; }
        const nx = -dy / len, ny = dx / len, nz = 0.55;
        const nLen = Math.sqrt(nx * nx + ny * ny + nz * nz);
        const nnx = nx / nLen, nny = ny / nLen, nnz = nz / nLen;
        seg.nDotL = Math.max(0.15, nnx * lx + nny * ly + nnz * lz);
        const dot = Math.max(0, nnx * hnx + nny * hny + nnz * hnz);
        seg.spec = Math.pow(dot, 4.0) * 0.6;
      }

      // Pass 0: Soft shadow beneath all stitches
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 0.12;
      ctx.lineWidth = TW * 1.8;
      ctx.strokeStyle = "rgba(10,5,0,1)";
      ctx.beginPath();
      for (const seg of segments) { ctx.moveTo(seg.x1 + 0.5, seg.y1 + 0.7); ctx.lineTo(seg.x2 + 0.5, seg.y2 + 0.7); }
      ctx.stroke();

      // Pass 1: Dark base (thread outline / between-stitch groove)
      ctx.globalAlpha = 1.0;
      ctx.lineWidth = TW * 1.05;
      for (const [ci, segs] of Object.entries(byColor)) {
        ctx.strokeStyle = darkerHex(threadColors[ci] ?? "#333");
        ctx.beginPath();
        for (const s of segs) { ctx.moveTo(s.x1, s.y1); ctx.lineTo(s.x2, s.y2); }
        ctx.stroke();
      }

      // Pass 2: Per-stitch lit body with direction-based shading
      ctx.lineWidth = TW * 0.82;
      for (const seg of segments) {
        const base = threadColors[seg.ci] ?? "#333";
        const [r, g, b] = parseHex(base);
        let [h, l, s] = rgbToHls(r, g, b);
        // Keep light threads light: ease off the diffuse darkening as lightness
        // rises so white/cream thread stays bright instead of going grey.
        if (l > 0.97) l = 0.97;
        const diffuse = seg.nDotL;
        let lit = 0.65 + 0.35 * diffuse;
        if (l > 0.6) lit = lit + (1 - lit) * ((l - 0.6) / 0.4) * 0.8;
        l = l * lit;
        s = Math.min(1, s + 0.08);
        const [rr, gg, bb] = hlsToRgb(h, l, s);
        ctx.strokeStyle = `rgb(${rr},${gg},${bb})`;
        ctx.globalAlpha = 1.0;
        ctx.beginPath();
        ctx.moveTo(seg.x1, seg.y1);
        ctx.lineTo(seg.x2, seg.y2);
        ctx.stroke();
      }

      // Pass 3: Specular highlight per stitch (thread sheen)
      ctx.lineWidth = TW * 0.45;
      for (const seg of segments) {
        if (seg.spec < 0.05) continue;
        const base = threadColors[seg.ci] ?? "#333";
        const amt = Math.round(seg.spec * 220);
        ctx.strokeStyle = lightenHex(base, amt);
        ctx.globalAlpha = Math.min(0.7, seg.spec * 1.2);
        ctx.beginPath();
        ctx.moveTo(seg.x1, seg.y1);
        ctx.lineTo(seg.x2, seg.y2);
        ctx.stroke();
      }

      // Pass 4: Fine bright center line (thread twist highlight)
      ctx.lineWidth = Math.max(0.3, TW * 0.15);
      ctx.globalAlpha = 0.2;
      for (const seg of segments) {
        if (seg.spec < 0.15) continue;
        ctx.strokeStyle = lightenHex(threadColors[seg.ci] ?? "#333", 240);
        ctx.globalAlpha = seg.spec * 0.35;
        ctx.beginPath();
        ctx.moveTo(seg.x1, seg.y1);
        ctx.lineTo(seg.x2, seg.y2);
        ctx.stroke();
      }

      ctx.restore();
    }
    // Per-layer stitch pattern preview is now rendered via SVG (vectorStitchElements)
    // so the canvas is only used for realStitchData and ghost previews below.

    // Ghost previews (A1 rail drawing, A2 split/cut) are now rendered
    // in the SVG vector layer (ghostPreviewElements) below.

    if (renderIdRef.current !== renderId) return;
    ctx.restore();
  }, [realStitchData, stitchToViewbox, zoom, BASE_W, BASE_H, colors, aiLoading, layoutMetrics]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => draw(), 16);
    return () => clearTimeout(t);
  }, [draw]);

  // ── Vector stitch SVG elements ─────────────────────────────────────────
  // Computes SVG <path> elements for each layer's stitch pattern, clipped
  // by the layer shape. This replaces the raster canvas for stitch preview
  // rendering — infinitely sharp at any zoom level.
  const vectorStitchElements = useMemo(() => {
    if (realStitchData || aiLoading) return null;

    const { vbX, vbY, vbW, vbH } = layoutMetrics;
    return buildStitchElements({
      svgJoined,
      svgSplit,
      stitchSettings,
      colors,
      layerRails,
      layerSelected,
      viewBox: { vbX, vbY, vbW, vbH },
      targetWidth,
      embroidery: embroideryRef.current,
    });
  }, [svgJoined, svgSplit, stitchSettings, colors, layerRails, layerSelected, realStitchData, aiLoading, layoutMetrics, targetWidth, embroidery?.layerStitchSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Ghost preview SVG elements ─────────────────────────────────────────
  // Live previews during rail drawing (A1), cut mode (A2a), and split mode (A2b).
  const ghostPreviewElements = useMemo(() => {
    if (realStitchData || !svgJoined) return null;
    const { vbW } = layoutMetrics;
    const ghostMmToVb = vbW / (targetWidth || 100);
    const elements = [];

    // A1: Ghost satin only while drawing Edge 2. A single edge does not define
    // a satin area; inventing a mirrored partner colors unrelated artwork.
    if (activeRailDrawing?.railIndex === 1 && activeRailDrawing.points.length >= 2) {
      try {
        const ari = activeRailDrawing;
        const layerIdx = ari.layerIndex;
        const layerColor = colors?.[layerIdx] || "#888888";
        const inProgressD = railNodesToD(ari.points);

        let partnerD = null;
        if (ari.railIndex === 1) {
          const pendingEntry = layerRails?.[layerIdx];
          const pendingPairs = pendingEntry?.pairs || [];
          const lastPair = pendingPairs[pendingPairs.length - 1];
          if (lastPair?.rail0) partnerD = lastPair.rail0;
        }

        if (partnerD) {
          const rail0D = partnerD;
          const rail1D = inProgressD;
          const ghostSatinSp = (stitchSettings.zigzag_spacing_mm || 0.4) * ghostMmToVb;
          const stitchD = computeSatinStitchD(rail0D, rail1D, ghostSatinSp, 1, vbW);
          if (stitchD) {
            const baseCol = threadBaseColor(layerColor);
            elements.push(
              <g key="ghost-rail" opacity="0.35">
                <path d={stitchD} fill="none" stroke={baseCol} strokeWidth={ghostSatinSp * 1.5} strokeLinecap="butt" />
              </g>
            );
          }
        }
      } catch (_) { /* non-critical */ }
    }

    // A2a: Ghost cut preview
    if (embroidery?.cutMode?.point1 && embroidery?.cutHoverPoint) {
      try {
        const cm = embroidery.cutMode;
        const hover = embroidery.cutHoverPoint;
        const piece = cm.pieces[cm.point1.pieceIndex];
        if (piece?.points) {
          const hit2 = findNearestPointHelper(piece.points, hover.x, hover.y);
          if (hit2 && validateSplitDistance(piece.points, cm.point1.index, hit2.index, 0.05)) {
            const result = cutShapeAtIndices(piece.points, cm.point1.index, hit2.index);
            if (result) {
              const pieceAD = pointsToClosedD(result.pieceA);
              const pieceBD = pointsToClosedD(result.pieceB);
              elements.push(
                <g key="ghost-cut">
                  <path d={pieceAD} fill="#8b5cf6" opacity="0.18" />
                  <path d={pieceBD} fill="#3b82f6" opacity="0.18" />
                </g>
              );
            }
          }
        }
      } catch (_) { /* non-critical */ }
    }

    // A2b: Ghost split preview
    if (embroidery?.splitMode?.point1 && embroidery?.splitHoverPoint) {
      try {
        const sm = embroidery.splitMode;
        const hover = embroidery.splitHoverPoint;
        const subpathData = embroidery.getSplitSubpaths?.();
        if (subpathData) {
          const hit = findNearestPointAcrossSubpaths(subpathData, hover.x, hover.y);
          if (hit && hit.subpathIndex === sm.point1.subpathIndex) {
            const sp = subpathData[hit.subpathIndex];
            if (validateSplitDistance(sp.points, sm.point1.index, hit.index)) {
              const rails = splitAtIndices(sp.points, sm.point1.index, hit.index);
              if (rails) {
                const layerColor = colors?.[sm.layerIndex] || "#888888";
                const ghostSplitSp = (stitchSettings.zigzag_spacing_mm || 0.4) * ghostMmToVb;
                const stitchD = computeSatinStitchD(rails.rail0, rails.rail1, ghostSplitSp, 1, vbW);
                if (stitchD) {
                  const baseCol = threadBaseColor(layerColor);
                  elements.push(
                    <g key="ghost-split" opacity="0.3">
                      <path d={stitchD} fill="none" stroke={baseCol} strokeWidth={ghostSplitSp * 1.5} strokeLinecap="butt" />
                    </g>
                  );
                }
              }
            }
          }
        }
      } catch (_) { /* non-critical */ }
    }

    return elements.length > 0 ? elements : null;
  }, [svgJoined, realStitchData, activeRailDrawing, layerRails, colors, stitchSettings, embroidery?.cutMode?.point1, embroidery?.cutHoverPoint, embroidery?.splitMode?.point1, embroidery?.splitHoverPoint, layoutMetrics]); // eslint-disable-line react-hooks/exhaustive-deps

  // Extract the current layer's SVG path data for the shape underlay
  const currentLayerPath = useMemo(() => {
    if (!svgJoined || currentLayerIdx == null) return null;
    const children = svgJoined.children || [];
    const child = children[currentLayerIdx];
    if (!child) return null;

    const paths = [];
    const extractPaths = (node) => {
      if (node.name === "path" && node.attributes?.d) {
        paths.push({
          d: node.attributes.d,
          fill: node.attributes.fill,
          stroke: node.attributes.stroke,
          fillRule: node.attributes["fill-rule"] || node.attributes.fillRule,
        });
      }
      if (node.children) node.children.forEach(extractPaths);
    };

    if (child.name === "g") {
      (child.children || []).forEach(extractPaths);
    } else {
      extractPaths(child);
    }
    return paths;
  }, [svgJoined, currentLayerIdx]);

  // Pre-sample layer path points once when the layer changes (DOM op must be in effect, not render).
  const layerPathSamplesRef = useRef([]);
  useEffect(() => {
    if (!currentLayerPath?.length) { layerPathSamplesRef.current = []; return; }
    const all = [];
    for (const { d } of currentLayerPath) {
      for (const subpath of splitCompoundD(d)) {
        try {
          const points = samplePathPoints(subpath, 500);
          const group = { start: all.length, count: points.length, closed: /z\s*$/i.test(subpath) };
          all.push(...points.map(p => ({ ...p, group })));
        } catch (_) {}
      }
    }
    layerPathSamplesRef.current = all;
  }, [currentLayerPath]);

  // Snap a viewBox point to the nearest pre-sampled point; also returns sample index.
  const snapToLayerPath = useCallback((pt) => {
    const samples = layerPathSamplesRef.current;
    if (!samples.length) return { snapped: pt, dist: Infinity, idx: -1 };
    let best = null, bestDist = Infinity, bestIdx = -1;
    for (let i = 0; i < samples.length; i++) {
      const sp = samples[i];
      const dist = Math.hypot(pt.x - sp.x, pt.y - sp.y);
      if (dist < bestDist) { bestDist = dist; best = sp; bestIdx = i; }
    }
    return { snapped: best || pt, dist: bestDist, idx: bestIdx };
  }, []);

  const [railMousePos, setRailMousePos] = useState(null);
  const wasSnappingRef = useRef(false);
  useEffect(() => {
    if (!isDrawingRail) setRailMousePos(null);
  }, [isDrawingRail]);

  const handleRailMouseMove = useCallback((e) => {
    if (!isDrawingRail) return;
    const pt = screenToSvgViewBox(e.clientX, e.clientY);
    if (!pt) return;
    const { scaleX } = layoutMetrics;
    const snapThresholdSvg = 10 / (zoomRef.current * scaleX);
    const { snapped, dist, idx } = snapToLayerPath(pt);
    const isSnapping = dist <= snapThresholdSvg;
    setRailMousePos(isSnapping ? { ...snapped, snapping: true } : { ...pt, snapping: false });

    // Haptic tick when snap state transitions (entering snap zone)
    if (isSnapping && !wasSnappingRef.current && navigator.vibrate) {
      navigator.vibrate(5);
    }
    wasSnappingRef.current = isSnapping;

  }, [isDrawingRail, screenToSvgViewBox, snapToLayerPath, layoutMetrics]);

  const handleRailClickSnapped = useCallback((e) => {
    if (!isDrawingRail || !onAddRailPoint) return;
    const pt = screenToSvgViewBox(e.clientX, e.clientY);
    if (!pt) return;
    const { scaleX } = layoutMetrics;
    const snapThresholdSvg = 10 / (zoomRef.current * scaleX);
    const { snapped, dist, idx } = snapToLayerPath(pt);
    const isSnapping = dist <= snapThresholdSvg;
    let usePoint = isSnapping ? snapped : pt;
    const placedCount = activeRailDrawing?.points?.length ?? 0;
    if (e.shiftKey && placedCount > 0) {
      const last = activeRailDrawing.points[placedCount - 1];
      const dx = Math.abs(usePoint.x - last.x);
      const dy = Math.abs(usePoint.y - last.y);
      usePoint = dx >= dy ? { x: usePoint.x, y: last.y } : { x: last.x, y: usePoint.y };
    }

    onAddRailPoint(usePoint.x, usePoint.y);
  }, [isDrawingRail, onAddRailPoint, screenToSvgViewBox, snapToLayerPath, layoutMetrics, activeRailDrawing?.points]);

  // Match SVGTrace's Pen tool: clicking creates a corner, while click-dragging
  // creates a node with mirrored Bézier handles.
  const railBezierDragRef = useRef(null);
  const suppressRailBezierClickRef = useRef(false);
  const previousRailPointCount = useRef(0);
  useEffect(() => {
    const count = activeRailDrawing?.points?.length || 0;
    if (count < previousRailPointCount.current && railBezierDragRef.current) {
      railBezierDragRef.current = null;
      suppressRailBezierClickRef.current = true;
    }
    previousRailPointCount.current = count;
  }, [activeRailDrawing?.points?.length]);

  const handleRailBezierPointerDown = useCallback((event) => {
    if (!isDrawingRail || event.button !== 0) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const point = screenToSvgViewBox(event.clientX, event.clientY);
    if (!point) return;
    const snapThresholdSvg = 10 / (zoomRef.current * layoutMetrics.scaleX);
    const { snapped, dist } = snapToLayerPath(point);
    railBezierDragRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
      anchor: dist <= snapThresholdSvg ? snapped : point,
      started: false,
    };
  }, [isDrawingRail, screenToSvgViewBox, layoutMetrics.scaleX, snapToLayerPath]);

  const handleRailBezierPointerMove = useCallback((event) => {
    handleRailMouseMove(event);
    const drag = railBezierDragRef.current;
    if (!drag || !isDrawingRail) return;
    const distance = Math.hypot(event.clientX - drag.clientX, event.clientY - drag.clientY);
    if (!drag.started && distance < 3) return;
    if (!drag.started) {
      drag.started = true;
      onAddRailPoint?.(drag.anchor.x, drag.anchor.y);
    }
    const handleOut = screenToSvgViewBox(event.clientX, event.clientY);
    if (!handleOut) return;
    const dx = handleOut.x - drag.anchor.x;
    const dy = handleOut.y - drag.anchor.y;
    onUpdateLastRailPoint?.({
      handleOut,
      handleIn: { x: drag.anchor.x - dx, y: drag.anchor.y - dy },
      smooth: true,
    });
  }, [handleRailMouseMove, isDrawingRail, onAddRailPoint, onUpdateLastRailPoint, screenToSvgViewBox]);

  const handleRailBezierPointerUp = useCallback((event) => {
    const drag = railBezierDragRef.current;
    if (!drag) return;
    event.stopPropagation();
    if (drag.started) suppressRailBezierClickRef.current = true;
    railBezierDragRef.current = null;
  }, []);

  const handleRailBezierClick = useCallback((event) => {
    event.stopPropagation();
    if (suppressRailBezierClickRef.current) {
      suppressRailBezierClickRef.current = false;
      return;
    }
    handleRailClickSnapped(event);
  }, [handleRailClickSnapped]);

  // Effective stroke widths for the overlay (in SVG viewBox units)
  const overlayStrokeW = useMemo(() => {
    const { scaleX } = layoutMetrics;
    const effectiveZoom = zoom * scaleX;
    return {
      // Keep rails precise enough to trace against the source artwork. The
      // controls remain generously sized; this only thins the visible path.
      rail: 1.8 / effectiveZoom,
      rung: 1.2 / effectiveZoom,
      dot: 4 / effectiveZoom,
      dotBorder: 1.8 / effectiveZoom,
      dashOn: 6 / effectiveZoom,
      dashOff: 4 / effectiveZoom,
      activeDashOn: 8 / effectiveZoom,
      activeDashOff: 4 / effectiveZoom,
      text: 6 / effectiveZoom,
    };
  }, [zoom, layoutMetrics]);

  // Build SVG elements for completed rails and in-progress drawing (now in viewBox coords)
  const railOverlayElements = useMemo(() => {
    const elements = [];
    // The rail guides are an editing aid for the Satin tool. Only show them while
    // the Stitch panel is open (or while actively drawing/selecting rails) so they
    // don't clutter the canvas when the user is just viewing or using another tool.
    if (!satinEditingActive && !activeRailDrawing && !railSelection) return elements;

    // Satin rails are only meaningful on a satin layer. A Fill/Running layer can
    // carry leftover rails (e.g. a duplicate from a reorder, or a sublayer cloned
    // from a satin parent); the stitch type is authoritative everywhere else, so
    // don't draw the editable rail guides on a layer that isn't satin. An
    // undefined/legacy type still shows them for backward compatibility.
    const curLs = embroideryRef.current?.getLayerSettings?.(currentLayerIdx);
    const curType =
      curLs?.passes?.[curLs?.activePassIndex || 0]?.stitchType || curLs?.passes?.[0]?.stitchType;
    if (curType && curType !== "satin") return elements;

    const entry = layerRails?.[currentLayerIdx];
    let pairs = entry?.pairs || [];
    if (pairs.length === 0 && entry?.rail0) {
      pairs = [{ rail0: entry.rail0, rail1: entry.rail1 }];
    }
    const sw = overlayStrokeW;
    (entry?.autoCuts || []).forEach((d, i) => elements.push(
      <path key={`auto-cut-${i}`} d={d} fill="none" stroke="#7C3AED"
        strokeWidth={sw.rail} strokeDasharray={`${sw.dashOn} ${sw.dashOff}`} />
    ));

    // Parse M/L path string → array of {x,y} (used for rungs, which are polylines)
    const parsePts = (d) => {
      const pts = [];
      const re = /[ML]\s*([\d.eE+-]+)[,\s]+([\d.eE+-]+)/gi;
      let m;
      while ((m = re.exec(d)) !== null) pts.push({ x: parseFloat(m[1]), y: parseFloat(m[2]) });
      return pts;
    };

    // Rails may be curve paths (auto-detected from SVG outlines). Dense-sample
    // those via getPointAtLength so the lane fill, endpoint dots, and badges
    // follow the actual rail geometry instead of degenerating to a small polygon
    // built from just the M start points.
    const hasCurves = (d) => /[CSQTAcsqta]/.test(d);
    const railToPts = (d) => {
      if (!d) return [];
      if (!hasCurves(d)) return parsePts(d);
      try { return samplePathPoints(d, 100); } catch (_) { return parsePts(d); }
    };

    pairs.forEach((pair, pairIdx) => {
      const color = getSatinColumnColor(pairIdx).stroke;
      const pts0 = railToPts(pair.rail0);
      const pts1 = railToPts(pair.rail1);

      // ── Lane fill between the two rails ──────────────────────────────
      if (pts0.length >= 2 && pts1.length >= 2) {
        const all = [...pts0, ...pts1.slice().reverse()];
        const fillD = `M${all[0].x},${all[0].y}` + all.slice(1).map((p) => `L${p.x},${p.y}`).join("") + "Z";
        elements.push(
          <path key={`lane-${pairIdx}`} d={fillD} fill={color} opacity="0.12" stroke="none" />
        );
      }

      // ── White outline (halo) then colored stroke for each rail ────────
      const drawRail = (d, railKey, dashed, railIdx) => {
        const isActiveEdit = railEditing?.pairIdx === pairIdx && railEditing?.railIndex === railIdx;
        elements.push(
          <path key={`${railKey}-halo`} d={d} stroke="#fff" strokeWidth={sw.rail * 2.6}
            fill="none" opacity="0.55" strokeLinecap="round" strokeLinejoin="round" />
        );
        elements.push(
          <path key={railKey} d={d} stroke={color} strokeWidth={sw.rail * (isActiveEdit ? 1.5 : 1.2)}
            fill="none" opacity={railEditing && !isActiveEdit ? 0.45 : 1}
            data-tutorial="satin-rail"
            data-rail-pair={pairIdx}
            data-rail-index={railIdx}
            strokeDasharray={dashed ? `${sw.dashOn} ${sw.dashOff}` : undefined}
            strokeLinecap="round" strokeLinejoin="round" />
        );
      };

      if (pair.rail0) drawRail(pair.rail0, `r0-${pairIdx}`, false, 0);
      if (pair.rail1) drawRail(pair.rail1, `r1-${pairIdx}`, true, 1);

      // ── Endpoint dots ─────────────────────────────────────────────────
      [[pts0, "0"], [pts1, "1"]].forEach(([pts, railId]) => {
        if (pts.length < 1) return;
        // Start: filled circle
        elements.push(
          <circle key={`ep-s-${pairIdx}-${railId}`} cx={pts[0].x} cy={pts[0].y}
            r={sw.dot * 1.1} fill={color} stroke="#fff" strokeWidth={sw.dotBorder} />
        );
        // End: hollow circle
        if (pts.length > 1) {
          const e = pts[pts.length - 1];
          elements.push(
            <circle key={`ep-e-${pairIdx}-${railId}`} cx={e.x} cy={e.y}
              r={sw.dot} fill="#fff" stroke={color} strokeWidth={sw.dotBorder * 1.4} />
          );
        }
      });

      // ── Pair number badge (when multiple pairs) ───────────────────────
      if (pairs.length > 1 && pts0.length >= 1) {
        const p = pts0[0];
        const r = sw.dot * 2;
        elements.push(
          <circle key={`badge-bg-${pairIdx}`} cx={p.x} cy={p.y - r * 2.2}
            r={r} fill={color} stroke="#fff" strokeWidth={sw.dotBorder} />
        );
        elements.push(
          <text key={`badge-lbl-${pairIdx}`} x={p.x} y={p.y - r * 2.2}
            textAnchor="middle" dominantBaseline="central"
            fill="#fff" fontSize={sw.text} fontWeight="700" fontFamily="sans-serif">
            {pairIdx + 1}
          </text>
        );
      }

      // ── Cross-guide (rung) lines ──────────────────────────────────────
      if (pair.rungs?.length > 0) {
        pair.rungs.forEach((rungD, rIdx) => {
          const rpts = parsePts(rungD);
          elements.push(
            <path key={`rung-halo-${pairIdx}-${rIdx}`} d={rungD}
              stroke="#fff" strokeWidth={sw.rung * 3.5} fill="none" opacity="0.6"
              strokeLinecap="round" />
          );
          elements.push(
            <path key={`rung-${pairIdx}-${rIdx}`} d={rungD}
              stroke={color} strokeWidth={sw.rung * 2} fill="none" opacity="0.95"
              strokeLinecap="round" />
          );
          rpts.forEach((pt, ptIdx) => {
            elements.push(
              <circle key={`rung-dot-${pairIdx}-${rIdx}-${ptIdx}`} cx={pt.x} cy={pt.y}
                r={sw.dot * 0.9} fill={color} stroke="#fff" strokeWidth={sw.dotBorder * 0.8} />
            );
          });
        });
      }
    });

    return elements;
  }, [layerRails, currentLayerIdx, overlayStrokeW, embroidery?.layerStitchSettings, satinEditingActive, activeRailDrawing, railSelection, railEditing]);

  // In-progress polyline for the rail being drawn (now in viewBox coords)
  const activeRailPath = useMemo(() => {
    if (!activeRailDrawing || activeRailDrawing.points.length < 1) return null;
    const pts = activeRailDrawing.points;
    const d = railNodesToD(pts);
    // Purple/pink stays distinct from SVGTrace's blue selection outline.
    const color = activeRailDrawing.railIndex === 0 ? "#7c3aed" : "#db2777";
    const sw = overlayStrokeW;
    return (
      <g>
        <path d={d} stroke="#fff" strokeWidth={sw.rail * 3}
          fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.95" />
        <path d={d} stroke={color} strokeWidth={sw.rail * 1.45}
          fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="1" />
        {pts.map((p, i) => (
          <g key={i}>
            {["handleIn", "handleOut"].map((part) => p[part] && (
              <g key={part}>
                <line x1={p.x} y1={p.y} x2={p[part].x} y2={p[part].y}
                  stroke={color} strokeWidth={sw.rung} opacity="0.65" />
                <circle cx={p[part].x} cy={p[part].y} r={sw.dot * 0.78}
                  fill={color} stroke="#fff" strokeWidth={sw.dotBorder} />
              </g>
            ))}
            <rect x={p.x - sw.dot} y={p.y - sw.dot} width={sw.dot * 2} height={sw.dot * 2}
              rx={sw.dot * 0.18} fill={i === 0 ? color : "#fff"}
              stroke={color} strokeWidth={sw.dotBorder} />
          </g>
        ))}
        {pts.length > 0 && (
          <text x={pts[pts.length - 1].x + sw.dot * 2.2} y={pts[pts.length - 1].y - sw.dot * 2.2}
            fill={color} stroke="#fff" strokeWidth={sw.text * 0.28} paintOrder="stroke"
            fontSize={sw.text * 1.45} fontWeight="800" fontFamily="sans-serif">
            EDGE {activeRailDrawing.railIndex + 1}
          </text>
        )}
      </g>
    );
  }, [activeRailDrawing, overlayStrokeW]);

  const [railBox, setRailBox] = useState(null);
  const railBoxCleanup = useRef(null);
  useEffect(() => () => railBoxCleanup.current?.(), [railEditing?.pairIdx, railEditing?.railIndex]);
  const startRailBox = (event) => {
    if (event.button !== 0 || spacePressedRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    const start = screenToSvgViewBox(event.clientX, event.clientY);
    if (!start) return;
    const previous = event.shiftKey ? (railEditing.selectedNodes || []) : [];
    onSelectRailNodes?.(previous);
    const move = (e) => {
      const end = screenToSvgViewBox(e.clientX, e.clientY);
      if (!end) return;
      const box = { x: Math.min(start.x, end.x), y: Math.min(start.y, end.y), width: Math.abs(end.x - start.x), height: Math.abs(end.y - start.y) };
      setRailBox(box);
      const selected = railEditing.nodes.flatMap((n, i) => n.x >= box.x && n.x <= box.x + box.width && n.y >= box.y && n.y <= box.y + box.height ? [i] : []);
      onSelectRailNodes?.([...new Set([...previous, ...selected])]);
    };
    const cleanup = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
    };
    const finish = () => { cleanup(); setRailBox(null); };
    railBoxCleanup.current?.();
    railBoxCleanup.current = cleanup;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
  };

  const editableRailOverlay = useMemo(() => {
    if (!railEditing) return null;
    const { layerIndex, pairIdx, railIndex, selectedNode } = railEditing;
    const nodes = railEditing.nodes;
    const selected = new Set(railEditing.selectedNodes || (selectedNode == null ? [] : [selectedNode]));
    if (!nodes?.length) return null;
    const sw = overlayStrokeW;
    const color = getSatinColumnColor(pairIdx).stroke;

    const startNodeDrag = (nodeIndex, part = "anchor") => (event) => {
      event.stopPropagation();
      event.preventDefault();
      if (event.shiftKey && part === "anchor") {
        const next = new Set(selected);
        if (next.has(nodeIndex)) next.delete(nodeIndex); else next.add(nodeIndex);
        onSelectRailNodes?.([...next]);
        return;
      }
      onSelectRailNode?.(nodeIndex);
      const original = nodes[nodeIndex];
      let nextNodes = nodes.map((node) => ({
        ...node,
        handleIn: node.handleIn ? { ...node.handleIn } : null,
        handleOut: node.handleOut ? { ...node.handleOut } : null,
      }));
      let latestD = railNodesToD(nextNodes);
      const onMove = (moveEvent) => {
        let point = screenToSvgViewBox(moveEvent.clientX, moveEvent.clientY);
        if (!point) return;
        // Shift locks movement to the dominant axis, useful for clean caps.
        if (moveEvent.shiftKey) {
          const origin = part === "anchor" ? original : original[part];
          const dx = Math.abs(point.x - origin.x);
          const dy = Math.abs(point.y - origin.y);
          point = dx >= dy ? { x: point.x, y: origin.y } : { x: origin.x, y: point.y };
        }
        nextNodes = nextNodes.map((item, index) => {
          if (index !== nodeIndex) return item;
          if (part === "anchor") {
            const dx = point.x - item.x;
            const dy = point.y - item.y;
            return {
              ...item,
              x: point.x,
              y: point.y,
              handleIn: item.handleIn ? { x: item.handleIn.x + dx, y: item.handleIn.y + dy } : null,
              handleOut: item.handleOut ? { x: item.handleOut.x + dx, y: item.handleOut.y + dy } : null,
            };
          }
          const opposite = part === "handleIn" ? "handleOut" : "handleIn";
          const updated = { ...item, [part]: point };
          if (item.smooth && item[opposite]) {
            updated[opposite] = { x: item.x * 2 - point.x, y: item.y * 2 - point.y };
          }
          return updated;
        });
        latestD = railNodesToD(nextNodes);
        onPreviewRailEdit?.(layerIndex, pairIdx, railIndex, latestD, nextNodes);
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        onCommitRailEdit?.(layerIndex, pairIdx, railIndex, latestD, nextNodes);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    };

    return (
      <g data-rail-node-editor="true">
        {nodes.slice(0, -1).map((node, index) => {
          const next = nodes[index + 1];
          const c1 = node.handleOut || node;
          const c2 = next.handleIn || next;
          const midpoint = (node.handleOut || next.handleIn)
            ? {
                x: (node.x + 3 * c1.x + 3 * c2.x + next.x) / 8,
                y: (node.y + 3 * c1.y + 3 * c2.y + next.y) / 8,
              }
            : { x: (node.x + next.x) / 2, y: (node.y + next.y) / 2 };
          return (
            <circle
              key={`insert-${index}`}
              cx={midpoint.x}
              cy={midpoint.y}
              r={sw.dot * 0.72}
              fill="#fff"
              stroke={color}
              strokeWidth={sw.dotBorder}
              opacity="0.9"
              pointerEvents="auto"
              style={{ cursor: "copy" }}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                onInsertRailNode?.(index, midpoint);
              }}
            />
          );
        })}
        {nodes.map((node, index) => (
          <g key={`node-${index}`}>
            {selectedNode === index && ["handleIn", "handleOut"].map((part) => node[part] && (
              <g key={part}>
                <line x1={node.x} y1={node.y} x2={node[part].x} y2={node[part].y}
                  stroke={color} strokeWidth={sw.rung} opacity="0.65" pointerEvents="none" />
                <circle cx={node[part].x} cy={node[part].y} r={sw.dot * 0.82}
                  fill={color} stroke="#fff" strokeWidth={sw.dotBorder}
                  pointerEvents="auto" style={{ cursor: "move" }}
                  onPointerDown={startNodeDrag(index, part)} />
              </g>
            ))}
            <rect
              x={node.x - sw.dot * (selected.has(index) ? 1.25 : 1)}
              y={node.y - sw.dot * (selected.has(index) ? 1.25 : 1)}
              width={sw.dot * (selected.has(index) ? 2.5 : 2)}
              height={sw.dot * (selected.has(index) ? 2.5 : 2)}
              rx={sw.dot * 0.18}
              fill={selected.has(index) ? color : "#fff"}
              stroke={color} strokeWidth={sw.dotBorder * 1.25}
              pointerEvents="auto" style={{ cursor: "move" }}
              onPointerDown={startNodeDrag(index)}
            />
          </g>
        ))}
      </g>
    );
  }, [railEditing, layerRails, overlayStrokeW, screenToSvgViewBox, onPreviewRailEdit, onCommitRailEdit, onInsertRailNode, onSelectRailNode, onSelectRailNodes]);

  const hasManualRungs = (() => {
    const entry = layerRails?.[currentLayerIdx];
    const pairs = entry?.pairs || [];
    return pairs.some((p) => p.rungs?.length > 0);
  })();
  // Show overlay whenever there are any rails (completed or in-progress)
  const hasCompletedRails = (() => {
    const entry = layerRails?.[currentLayerIdx];
    if (!entry) return false;
    const pairs = entry?.pairs || [];
    return pairs.some((p) => p.rail0 || p.rail1) || !!(entry?.rail0 || entry?.rail1);
  })();
  const hasRailOverlay = hasEditableLayer && (isDrawingRail || isEditingRail || activeRailPath || isDrawingRung || hasManualRungs || hasCompletedRails);

  useEffect(() => {
    if (!isEditingRail) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape" || event.key === "Enter") {
        // The toolbar owns exiting edit mode; avoid stealing global undo keys.
        return;
      }
      if (event.target?.closest?.("input, textarea, select, [contenteditable='true']")) return;
      if ((event.key === "Backspace" || event.key === "Delete") && (railEditing?.selectedNodes?.length || railEditing?.selectedNode != null)) {
        event.preventDefault();
        onDeleteRailNode?.();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isEditingRail, railEditing?.selectedNode, railEditing?.selectedNodes, onDeleteRailNode]);

  // Track snapped mouse position in viewBox coords for rung drawing preview
  const [rungMousePos, setRungMousePos] = useState(null);
  // Which rail pair's on-canvas controls are expanded. Default collapsed to a
  // small numbered dot so a design with many auto-detected pairs isn't buried
  // under a wall of pills/buttons; hovering a dot reveals its full controls.
  const [hoveredGuideIdx, setHoveredGuideIdx] = useState(null);
  const handleRungMouseMove = useCallback((e) => {
    if (!isDrawingRung) return;
    const pt = screenToSvgViewBox(e.clientX, e.clientY);
    if (pt) setRungMousePos(snapToActivePair(pt));
  }, [isDrawingRung, screenToSvgViewBox, snapToActivePair]);

  const activeRungOverlay = useMemo(() => {
    if (!isDrawingRung) return null;
    const sw = overlayStrokeW;
    const activeColor = getSatinColumnColor(activeRungDrawing?.pairIdx || 0).stroke;
    const elements = [];
    const sp = activeRungDrawing?.startPoint;
    const snap = rungMousePos;

    // Snap indicator — show even before first click so user can see where it will land
    if (snap) {
      elements.push(
        <circle key="rung-snap" cx={snap.x} cy={snap.y} r={sw.dot * 1.4}
          fill="none" stroke={activeColor} strokeWidth={sw.dotBorder * 1.5}
          strokeDasharray={`${sw.dashOn * 0.3} ${sw.dashOff * 0.3}`} opacity="0.9" />
      );
      elements.push(
        <circle key="rung-snap-inner" cx={snap.x} cy={snap.y} r={sw.dot * 0.5}
          fill={activeColor} stroke="none" opacity="0.9" />
      );
    }

    if (sp) {
      // Placed start dot
      elements.push(
        <circle key="rung-start" cx={sp.x} cy={sp.y} r={sw.dot * 1.2}
          fill={activeColor} stroke="#fff" strokeWidth={sw.dotBorder} />
      );
      // Preview line from start to current snapped position
      if (snap) {
        elements.push(
          <line key="rung-preview-halo" x1={sp.x} y1={sp.y} x2={snap.x} y2={snap.y}
            stroke="#fff" strokeWidth={sw.rung * 3} opacity="0.5" strokeLinecap="round" />
        );
        elements.push(
          <line key="rung-preview" x1={sp.x} y1={sp.y} x2={snap.x} y2={snap.y}
            stroke={activeColor} strokeWidth={sw.rung * 1.8}
            strokeDasharray={`${sw.dashOn * 0.5} ${sw.dashOff * 0.5}`}
            strokeLinecap="round" opacity="0.85" />
        );
      }
    }
    return elements.length > 0 ? elements : null;
  }, [isDrawingRung, activeRungDrawing?.startPoint, rungMousePos, overlayStrokeW]);

  // Build selectable path elements for rail selection mode
  const selectablePathOverlay = useMemo(() => {
    if (!isSelectingRail || !svgSplit) return null;
    const layerEl = svgSplit.children?.[railSelection.layerIndex];
    let layerChildren = layerEl?.children;
    let subpathDs = null;
    if (!layerChildren?.length) {
      const compoundD = layerEl?.attributes?.d;
      subpathDs = splitCompoundD(compoundD);
      if (!subpathDs.length) return null;
    }
    const items = subpathDs || layerChildren;
    const layerColor = colors?.[railSelection.layerIndex] || "#888";
    const sw = overlayStrokeW;
    return items.map((item, idx) => {
      const d = subpathDs ? item : item.attributes?.d;
      if (!d) return null;
      const isRail0 = railSelection.rail0PathIdx === idx;
      const isRail1 = railSelection.rail1PathIdx === idx;
      const isHovered = hoveredPathIdx === idx;
      let strokeColor = layerColor;
      let strokeOpacity = 0.4;
      let strokeW = sw.rail;
      if (isRail0) { strokeColor = "#3b82f6"; strokeOpacity = 0.9; strokeW = sw.rail * 1.5; }
      else if (isRail1) { strokeColor = "#ec4899"; strokeOpacity = 0.9; strokeW = sw.rail * 1.5; }
      else if (isHovered) { strokeColor = railSelection.selectingRail === 0 ? "#3b82f6" : "#ec4899"; strokeOpacity = 0.7; strokeW = sw.rail * 1.3; }
      return (
        <g key={`sel-${idx}`}
          data-tutorial="satin-selectable-path"
          data-path-index={idx}
          onMouseEnter={() => setHoveredPathIdx(idx)}
          onMouseLeave={() => setHoveredPathIdx(null)}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onSelectPathAsRail?.(idx); }}
          onPointerDown={(e) => { e.stopPropagation(); }}
          style={{ cursor: "pointer" }}
        >
          {/* Invisible wide hit area */}
          <path d={d} fill="transparent" stroke="transparent" strokeWidth={sw.rail * 6} />
          {/* Visible stroke */}
          <path d={d} fill="none" stroke={strokeColor} strokeWidth={strokeW}
            opacity={strokeOpacity} style={{ pointerEvents: "none", transition: "all 0.12s ease" }} />
        </g>
      );
    });
  }, [isSelectingRail, svgSplit, railSelection, hoveredPathIdx, colors, overlayStrokeW, onSelectPathAsRail]);

  // Smart Cut overlay — renders individual subpaths; clicking one removes it from the layer.
  const smartCutOverlay = useMemo(() => {
    if (!smartCutActive || smartCutLayerIdx == null || !svgSplit) return null;
    const layerEl = svgSplit.children?.[smartCutLayerIdx];
    let subpathDs = null;
    const layerChildren = layerEl?.children;
    if (!layerChildren?.length) {
      const compoundD = layerEl?.attributes?.d;
      subpathDs = splitCompoundD(compoundD);
      if (!subpathDs.length) return null;
    }
    const items = subpathDs || layerChildren;
    const sw = overlayStrokeW;
    return items.map((item, idx) => {
      const d = subpathDs ? item : item.attributes?.d;
      if (!d) return null;
      const isHovered = hoveredPathIdx === idx;
      return (
        <g
          key={`smartcut-${idx}`}
          onMouseEnter={() => setHoveredPathIdx(idx)}
          onMouseLeave={() => setHoveredPathIdx(null)}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onSmartCutSubpath?.(idx); }}
          onPointerDown={(e) => e.stopPropagation()}
          style={{ cursor: "crosshair" }}
        >
          {/* Invisible wide hit area */}
          <path d={d} fill="transparent" stroke="transparent" strokeWidth={sw.rail * 8} />
          {/* Filled highlight */}
          <path
            d={d}
            fill={isHovered ? "rgba(239,68,68,0.25)" : "rgba(239,68,68,0.08)"}
            stroke={isHovered ? "#ef4444" : "rgba(239,68,68,0.4)"}
            strokeWidth={isHovered ? sw.rail * 1.5 : sw.rail}
            style={{ pointerEvents: "none", transition: "all 0.1s ease" }}
          />
        </g>
      );
    });
  }, [smartCutActive, smartCutLayerIdx, svgSplit, hoveredPathIdx, overlayStrokeW, onSmartCutSubpath]);

  // Zoom toward the container center — matching SVG editor button behavior.
  const zoomTowardCenter = useCallback((factor) => {
    const container = containerRef.current;
    const rect = container ? container.getBoundingClientRect() : null;
    const cx = rect ? rect.width / 2 : BASE_W / 2;
    const cy = rect ? rect.height / 2 : BASE_H / 2;
    const prev = zoomRef.current;
    const next = Math.max(0.3, Math.min(20, prev * factor));
    const scale = next / prev;
    const newPan = {
      x: cx - scale * (cx - panRef.current.x),
      y: cy - scale * (cy - panRef.current.y),
    };
    panRef.current = newPan;
    zoomRef.current = next;
    setPan(newPan);
    setZoom(next);
  }, [BASE_W, BASE_H]);

  return (
    <div
      ref={containerRef}
      data-tutorial="stitch-canvas"
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
          cursor: spacePressed
          ? (isDragging ? "grabbing" : "grab")
          : (isDrawingRail || isDrawingRung || isSplitting || isCutting) ? "crosshair"
          : isEditingRail ? "default"
          : isSelectingRail ? "crosshair"
          : smartCutActive ? "crosshair"
          : isDragging ? "grabbing" : "grab",
        userSelect: "none",
        touchAction: "none",
        background: "linear-gradient(135deg, #f5efe5 0%, #f0e9db 30%, #ebe2d2 70%, #e4d9c6 100%)",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={(e) => {
        onPointerMove(e);
        if (suppressNextDrawClickRef.current) return;
        if (isDrawingRail) handleRailMouseMove(e);
        else if (isDrawingRung) handleRungMouseMove(e);
        else if (isSplitting) handleSplitMouseMove(e);
        else if (isCutting) handleCutMouseMove(e);
      }}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClick={(e) => {
        if (suppressNextDrawClickRef.current) {
          suppressNextDrawClickRef.current = false;
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (isDrawingRail) handleRailClickSnapped(e);
        else if (isDrawingRung) handleRungClick(e);
        else if (isSplitting) handleSplitClick(e);
        else if (isCutting) handleCutClick(e);
      }}
      onDoubleClick={isDrawingRail ? handleRailDblClick : undefined}
    >
      {/* ── Design SVG ──────────────────────────────────────────────────────────
          Fills the full container (like the SVG editor's outer <svg>).
          Background is on the container via CSS. An inner <g> holds the
          pan/zoom transform so the design stays stable on window resize.
      ─────────────────────────────────────────────────────────────────────── */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
          pointerEvents: "none",
          overflow: "visible",
        }}
      >
        {/* Pan/zoom group — only this moves, container SVG stays full-size */}
        <g ref={transformGroupRef} transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
          {/* Hoop area — subtle white fill + dashed border */}
          <rect
            x={layoutMetrics.CX - layoutMetrics.hoopPxW / 2}
            y={layoutMetrics.CY - layoutMetrics.hoopPxH / 2}
            width={layoutMetrics.hoopPxW}
            height={layoutMetrics.hoopPxH}
            fill="rgba(255,255,255,0.30)"
            stroke="rgba(120,100,70,0.28)"
            strokeWidth={1.5}
            strokeDasharray="6 4"
          />
          <text
            x={layoutMetrics.CX}
            y={layoutMetrics.CY + layoutMetrics.hoopPxH / 2 + 14}
            textAnchor="middle"
            fontSize={9}
            fill="rgba(120,100,70,0.45)"
            fontFamily="Inter, system-ui, sans-serif"
            fontWeight="500"
          >
            {hoopWidth || 100} × {hoopHeight || 100} mm
          </text>
          {((targetWidth || 0) !== (hoopWidth || 100) || (targetHeight || 0) !== (hoopHeight || 100)) &&
            (targetWidth || 0) > 0 && (
            <text
              x={layoutMetrics.CX}
              y={layoutMetrics.CY + layoutMetrics.hoopPxH / 2 + 26}
              textAnchor="middle"
              fontSize={8}
              fill="rgba(120,100,70,0.38)"
              fontFamily="Inter, system-ui, sans-serif"
              fontWeight="500"
            >
              design: {targetWidth} × {targetHeight} mm
            </text>
          )}

          {/* Locked, non-exporting source artwork backdrop. The crop transform
              keeps the original pixels registered to the traced SVG viewBox. */}
          {referenceArtwork && referenceArtworkVisible && (() => {
            const href = referenceArtwork.url || referenceArtwork.dataUrl;
            if (!href) return null;
            const registeredArtwork = resolveReferenceArtworkRegistration(referenceArtwork, svgJoined);
            const artworkCrop = registeredArtwork.crop || {};
            const sourceW = Number(registeredArtwork.sourceWidth) || Number(artworkCrop.width) || layoutMetrics.vbW;
            const sourceH = Number(registeredArtwork.sourceHeight) || Number(artworkCrop.height) || layoutMetrics.vbH;
            const cropW = Number(artworkCrop.width) || sourceW;
            const cropH = Number(artworkCrop.height) || sourceH;
            const cropX = Number(artworkCrop.x) || 0;
            const cropY = Number(artworkCrop.y) || 0;
            const scaleX = layoutMetrics.vbW / cropW;
            const scaleY = layoutMetrics.vbH / cropH;
            return (
              <svg
                x={layoutMetrics.designLeft}
                y={layoutMetrics.designTop}
                width={layoutMetrics.dw}
                height={layoutMetrics.dh}
                viewBox={`${layoutMetrics.vbX} ${layoutMetrics.vbY} ${layoutMetrics.vbW} ${layoutMetrics.vbH}`}
                preserveAspectRatio="none"
                overflow="hidden"
              >
                <image
                  href={href}
                  x={layoutMetrics.vbX - cropX * scaleX}
                  y={layoutMetrics.vbY - cropY * scaleY}
                  width={sourceW * scaleX}
                  height={sourceH * scaleY}
                  preserveAspectRatio="none"
                  opacity={referenceArtworkOpacity}
                  style={{ pointerEvents: "none" }}
                />
              </svg>
            );
          })()}

          {/* Keep artwork hit targets, but do not let its solid color hide thread gaps or negative expansion. */}
          {!aiLoading && svgLayerElements && (
            <svg
              x={layoutMetrics.designLeft}
              y={layoutMetrics.designTop}
              width={layoutMetrics.dw}
              height={layoutMetrics.dh}
              viewBox={`${layoutMetrics.vbX} ${layoutMetrics.vbY} ${layoutMetrics.vbW} ${layoutMetrics.vbH}`}
              preserveAspectRatio="none"
              overflow="hidden"
              style={{ opacity: vectorStitchElements ? 0 : referenceArtwork && referenceArtworkVisible ? 0.58 : 1 }}
            >
              {svgLayerElements}
            </svg>
          )}

          {/* No design placeholder */}
          {!aiLoading && !svgJoined && !svgSplit && (
            <text
              x={layoutMetrics.CX}
              y={layoutMetrics.CY}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={16}
              fill="rgba(100,80,60,0.20)"
              fontFamily="Inter, system-ui, sans-serif"
              fontWeight="500"
            >
              No design loaded
            </text>
          )}
        </g>
      </svg>

      {/* Keep thread rendering in a viewport-sized SVG. Native SVG transforms
          repaint patterns and paths at the current zoom instead of magnifying
          the cached bitmap of a CSS-transformed compositing layer. */}
      {(vectorStitchElements || ghostPreviewElements) && (
        <svg
          data-thread-preview="true"
          className={sewInActive ? "sew-in-active" : ""}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            overflow: "hidden",
          }}
        >
          <g data-pan-zoom="vector" transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
            <svg
              x={layoutMetrics.designLeft}
              y={layoutMetrics.designTop}
              width={layoutMetrics.dw}
              height={layoutMetrics.dh}
              viewBox={`${layoutMetrics.vbX} ${layoutMetrics.vbY} ${layoutMetrics.vbW} ${layoutMetrics.vbH}`}
              preserveAspectRatio="none"
              overflow="visible"
            >
              {vectorStitchElements}
              {ghostPreviewElements}
            </svg>
          </g>
        </svg>
      )}

      {/* ── Stitch simulation canvas (DST/machine stitch data only) ────────────
          Only renders when realStitchData is present (actual machine stitch file).
          The preview patterns above handle the design-time visualization.
      ─────────────────────────────────────────────────────────────────────── */}
      <canvas
        ref={canvasRef}
        data-pan-zoom="canvas"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          display: realStitchData ? "block" : "none",
          width: `${BASE_W}px`,
          height: `${BASE_H}px`,
          transformOrigin: "0 0",
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          willChange: "transform",
          imageRendering: "auto",
          pointerEvents: "none",
        }}
      />

      {/* Keep manual rails in a viewport-sized SVG, like the design below.
          Native SVG transforms redraw the paths and nodes at the current zoom;
          scaling a composited CSS layer can stretch a cached bitmap instead. */}
      {hasRailOverlay && (
        <svg
          data-tutorial="manual-rail-overlay"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            overflow: "visible",
          }}
        >
          <g data-pan-zoom="vector" transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
            <svg
              x={layoutMetrics.designLeft}
              y={layoutMetrics.designTop}
              width={layoutMetrics.dw}
              height={layoutMetrics.dh}
              viewBox={`${layoutMetrics.vbX} ${layoutMetrics.vbY} ${layoutMetrics.vbW} ${layoutMetrics.vbH}`}
              preserveAspectRatio="none"
              style={{ pointerEvents: isDrawingRail ? "auto" : "none", overflow: "visible" }}
              onPointerDown={isDrawingRail ? handleRailBezierPointerDown : undefined}
              onPointerMove={isDrawingRail ? handleRailBezierPointerMove : undefined}
              onPointerUp={isDrawingRail ? handleRailBezierPointerUp : undefined}
              onPointerCancel={isDrawingRail ? handleRailBezierPointerUp : undefined}
              onClick={isDrawingRail ? handleRailBezierClick : undefined}
              onDoubleClick={isDrawingRail ? handleRailDblClick : undefined}
            >
              {/* Shape underlay for rail drawing — only when no stitch data (pre-conversion) */}
              {isDrawingRail && !realStitchData && currentLayerPath?.map((p, i) => {
                const layerColor = colors?.[currentLayerIdx] || "#888";
                return (
                  <path
                    key={`underlay-${i}`}
                    d={p.d}
                    fill={p.fill && p.fill !== "none" ? p.fill : layerColor}
                    fillRule={p.fillRule || "evenodd"}
                    stroke={p.stroke && p.stroke !== "none" ? p.stroke : "none"}
                    opacity="0.25"
                  />
                );
              })}
              {/* Snap indicator during rail drawing */}
              {isDrawingRail && railMousePos && (() => {
                const sw = overlayStrokeW;
                return railMousePos.snapping ? (
                  <>
                    <circle cx={railMousePos.x} cy={railMousePos.y} r={sw.dot * 1.5}
                      fill="none" stroke="#22c55e" strokeWidth={sw.dotBorder * 1.5}
                      strokeDasharray={`${sw.dashOn * 0.3} ${sw.dashOff * 0.3}`} opacity="0.9" />
                    <circle cx={railMousePos.x} cy={railMousePos.y} r={sw.dot * 0.55}
                      fill="#22c55e" stroke="none" opacity="0.9" />
                  </>
                ) : (
                  <circle cx={railMousePos.x} cy={railMousePos.y} r={sw.dot * 0.7}
                    fill="#7C3AED" stroke="#fff" strokeWidth={sw.dotBorder} opacity="0.7" />
                );
              })()}
              {railOverlayElements}
              {activeRailPath}
              {isEditingRail && <rect data-rail-selection-surface="true" x={layoutMetrics.vbX - 10000} y={layoutMetrics.vbY - 10000} width="20000" height="20000" fill="transparent" pointerEvents="all" style={{ cursor: "crosshair" }} onPointerDown={startRailBox} />}
              {editableRailOverlay}
              {railBox && <rect {...railBox} fill="rgba(139,92,246,0.15)" stroke="#8b5cf6" strokeWidth={overlayStrokeW.rung} pointerEvents="none" />}
              {activeRungOverlay}
            </svg>
          </g>
        </svg>
      )}

      {/* A3b: Draggable split-point handles for auto-detected rails */}
      {hasEditableLayer && !isDrawingRail && !isDrawingRung && autoDetectMeta?.[currentLayerIdx]?.points && (() => {
        const meta = autoDetectMeta[currentLayerIdx];
        const pt1 = meta.points[meta.idx1];
        const pt2 = meta.points[meta.idx2];
        if (!pt1 || !pt2) return null;
        const { vbX, vbY, scaleX, scaleY, designLeft, designTop } = layoutMetrics;
        const toScreen = (p) => ({
          x: (p.x - vbX) * scaleX * zoom + designLeft * zoom + pan.x,
          y: (p.y - vbY) * scaleY * zoom + designTop * zoom + pan.y,
        });
        const s1 = toScreen(pt1), s2 = toScreen(pt2);
        const handleR = Math.max(8, 12 * zoom);

        const onDragStart = (handleId) => (e) => {
          e.stopPropagation();
          e.preventDefault();
          const onMove = (ev) => {
            const { vbX: vx, vbY: vy, scaleX: sx, scaleY: sy, designLeft: dl, designTop: dt } = layoutMetrics;
            const svgX = ((ev.clientX - pan.x) / zoom - dl) / sx + vx;
            const svgY = ((ev.clientY - pan.y) / zoom - dt) / sy + vy;
            onDragSplitPoint?.(currentLayerIdx, handleId, svgX, svgY);
          };
          const onUp = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
          };
          window.addEventListener("pointermove", onMove);
          window.addEventListener("pointerup", onUp);
        };

        return (
          <>
            {[{ s: s1, id: 1 }, { s: s2, id: 2 }].map(({ s, id }) => (
              <div
                key={`drag-handle-${id}`}
                onPointerDown={onDragStart(id)}
                style={{
                  position: "absolute",
                  left: `${s.x - handleR}px`,
                  top: `${s.y - handleR}px`,
                  width: `${handleR * 2}px`,
                  height: `${handleR * 2}px`,
                  borderRadius: "50%",
                  background: "rgba(245, 158, 11, 0.9)",
                  border: "2.5px solid #fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                  cursor: "grab",
                  zIndex: 35,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  pointerEvents: "auto",
                  touchAction: "none",
                }}
              >
                <svg width={handleR * 0.7} height={handleR * 0.7} viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="2.5" fill="#fff" />
                </svg>
              </div>
            ))}
          </>
        );
      })()}

      {/* SVG overlay for path selection (click-to-select rails) */}
      {isSelectingRail && selectablePathOverlay && (
        <svg
          data-pan-zoom="design"
          data-dl={layoutMetrics.designLeft}
          data-dt={layoutMetrics.designTop}
          viewBox={`${layoutMetrics.vbX} ${layoutMetrics.vbY} ${layoutMetrics.vbW} ${layoutMetrics.vbH}`}
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: `${layoutMetrics.dw}px`,
            height: `${layoutMetrics.dh}px`,
            transformOrigin: "0 0",
            transform: `translate(${pan.x + layoutMetrics.designLeft * zoom}px, ${pan.y + layoutMetrics.designTop * zoom}px) scale(${zoom})`,
            pointerEvents: "auto",
            overflow: "visible",
            willChange: "transform",
          }}
        >
          {/* Faded fill underlay so paths are visible against the canvas */}
          {(() => {
            const layerEl = svgSplit?.children?.[railSelection?.layerIndex];
            const children = layerEl?.children;
            const layerColor = colors?.[railSelection?.layerIndex] || "#888";
            if (children?.length) {
              return children.map((child, idx) => {
                const d = child.attributes?.d;
                if (!d) return null;
                return <path key={`underlay-sel-${idx}`} d={d} fill={layerColor} stroke="none" opacity="0.15" />;
              });
            }
            return splitCompoundD(layerEl?.attributes?.d).map((d, idx) => (
              <path key={`underlay-sel-${idx}`} d={d} fill={layerColor} stroke="none" opacity="0.15" />
            ));
          })()}
          {selectablePathOverlay}
        </svg>
      )}

      {/* SVG overlay for smart cut mode — click a subpath to delete it */}
      {smartCutActive && smartCutOverlay && (
        <svg
          data-pan-zoom="design"
          data-dl={layoutMetrics.designLeft}
          data-dt={layoutMetrics.designTop}
          viewBox={`${layoutMetrics.vbX} ${layoutMetrics.vbY} ${layoutMetrics.vbW} ${layoutMetrics.vbH}`}
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: `${layoutMetrics.dw}px`,
            height: `${layoutMetrics.dh}px`,
            transformOrigin: "0 0",
            transform: `translate(${pan.x + layoutMetrics.designLeft * zoom}px, ${pan.y + layoutMetrics.designTop * zoom}px) scale(${zoom})`,
            pointerEvents: "auto",
            overflow: "visible",
            willChange: "transform",
          }}
        >
          {smartCutOverlay}
        </svg>
      )}

      {/* SVG overlay for split mode (click-to-split path) */}
      {isSplitting && embroidery.splitMode && (
        <svg
          data-pan-zoom="design"
          data-dl={layoutMetrics.designLeft}
          data-dt={layoutMetrics.designTop}
          viewBox={`${layoutMetrics.vbX} ${layoutMetrics.vbY} ${layoutMetrics.vbW} ${layoutMetrics.vbH}`}
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: `${layoutMetrics.dw}px`,
            height: `${layoutMetrics.dh}px`,
            transformOrigin: "0 0",
            transform: `translate(${pan.x + layoutMetrics.designLeft * zoom}px, ${pan.y + layoutMetrics.designTop * zoom}px) scale(${zoom})`,
            pointerEvents: "none",
            overflow: "visible",
            willChange: "transform",
          }}
        >
          {/* Shape outline highlight — one path per contour, matching the
              subpath indices split mode itself works in, so the tutorial can
              measure the shape it is asking the learner to cut across. */}
          {splitCompoundD(embroidery.splitMode.pathD).map((d, idx) => (
            <path
              key={`split-shape-${idx}`}
              data-tutorial="satin-split-shape"
              data-subpath-index={idx}
              d={d}
              fill="none"
              stroke="#f59e0b"
              strokeWidth={overlayStrokeW.rail * 1.5}
              strokeDasharray={`${overlayStrokeW.dashOn} ${overlayStrokeW.dashOff}`}
              opacity="0.6"
            />
          ))}
          {/* First click point */}
          {embroidery.splitMode.point1 && (
            <circle
              cx={embroidery.splitMode.point1.x}
              cy={embroidery.splitMode.point1.y}
              r={overlayStrokeW.dot * 1.5}
              fill="#f59e0b"
              stroke="#fff"
              strokeWidth={overlayStrokeW.dotBorder}
            />
          )}
          {/* Hover point */}
          {embroidery.splitHoverPoint && (
            <circle
              cx={embroidery.splitHoverPoint.x}
              cy={embroidery.splitHoverPoint.y}
              r={overlayStrokeW.dot}
              fill={embroidery.splitMode.point1 ? "#ef4444" : "#f59e0b"}
              stroke="#fff"
              strokeWidth={overlayStrokeW.dotBorder}
              opacity="0.7"
            />
          )}
        </svg>
      )}

      {/* Persistent pieces overlay (after cutting, when not in cut mode) */}
      {!isCutting && (layerPieces?.[currentLayerIdx] || []).filter(p => (p.d?.match(/-?\d+\.?\d*/g) || []).length > 8).length > 1 && (
        <svg
          data-pan-zoom="design"
          data-dl={layoutMetrics.designLeft}
          data-dt={layoutMetrics.designTop}
          viewBox={`${layoutMetrics.vbX} ${layoutMetrics.vbY} ${layoutMetrics.vbW} ${layoutMetrics.vbH}`}
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: `${layoutMetrics.dw}px`,
            height: `${layoutMetrics.dh}px`,
            transformOrigin: "0 0",
            transform: `translate(${pan.x + layoutMetrics.designLeft * zoom}px, ${pan.y + layoutMetrics.designTop * zoom}px) scale(${zoom})`,
            pointerEvents: "none",
            overflow: "visible",
            willChange: "transform",
          }}
        >
          {(layerPieces[currentLayerIdx] || [])
            .filter(p => (p.d?.match(/-?\d+\.?\d*/g) || []).length > 8)
            .map((piece, idx) => {
              const hues = ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#ec4899"];
              const color = hues[idx % hues.length];
              return (
                <path
                  key={`piece-outline-${idx}`}
                  d={piece.d}
                  fill={color}
                  fillOpacity={0.06}
                  stroke={color}
                  strokeWidth={overlayStrokeW.rail * 0.7}
                  strokeDasharray={`${overlayStrokeW.dashOn} ${overlayStrokeW.dashOff}`}
                  opacity={0.4}
                />
              );
            })}
        </svg>
      )}

      {/* SVG overlay for cut mode (slice shape into pieces) */}
      {isCutting && embroidery.cutMode && (
        <svg
          data-pan-zoom="design"
          data-dl={layoutMetrics.designLeft}
          data-dt={layoutMetrics.designTop}
          viewBox={`${layoutMetrics.vbX} ${layoutMetrics.vbY} ${layoutMetrics.vbW} ${layoutMetrics.vbH}`}
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: `${layoutMetrics.dw}px`,
            height: `${layoutMetrics.dh}px`,
            transformOrigin: "0 0",
            transform: `translate(${pan.x + layoutMetrics.designLeft * zoom}px, ${pan.y + layoutMetrics.designTop * zoom}px) scale(${zoom})`,
            pointerEvents: "none",
            overflow: "visible",
            willChange: "transform",
          }}
        >
          {/* Render each piece with alternating colors */}
          {embroidery.cutMode.pieces.map((piece, idx) => {
            const hues = ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#ec4899"];
            const color = hues[idx % hues.length];
            return (
              <path
                key={`cut-piece-${idx}`}
                d={piece.d}
                fill={color}
                fillOpacity={0.15}
                stroke={color}
                strokeWidth={overlayStrokeW.rail}
                strokeDasharray={`${overlayStrokeW.dashOn} ${overlayStrokeW.dashOff}`}
                opacity={0.7}
              />
            );
          })}
          {/* Cut-line preview */}
          {embroidery.cutMode.point1 && embroidery.cutHoverPoint && (
            <line
              x1={embroidery.cutMode.point1.x}
              y1={embroidery.cutMode.point1.y}
              x2={embroidery.cutHoverPoint.x}
              y2={embroidery.cutHoverPoint.y}
              stroke="#8b5cf6"
              strokeWidth={overlayStrokeW.rail * 1.5}
              strokeDasharray={`${overlayStrokeW.dashOn * 0.5} ${overlayStrokeW.dashOff * 0.5}`}
              opacity="0.7"
            />
          )}
          {/* First click point */}
          {embroidery.cutMode.point1 && (
            <circle
              cx={embroidery.cutMode.point1.x}
              cy={embroidery.cutMode.point1.y}
              r={overlayStrokeW.dot * 1.5}
              fill="#8b5cf6"
              stroke="#fff"
              strokeWidth={overlayStrokeW.dotBorder}
            />
          )}
          {/* Hover point */}
          {embroidery.cutHoverPoint && (
            <circle
              cx={embroidery.cutHoverPoint.x}
              cy={embroidery.cutHoverPoint.y}
              r={overlayStrokeW.dot}
              fill={embroidery.cutMode.point1 ? "#ef4444" : "#8b5cf6"}
              stroke="#fff"
              strokeWidth={overlayStrokeW.dotBorder}
              opacity="0.7"
            />
          )}
        </svg>
      )}

      {referenceArtwork && (
        <div
          onPointerDown={(event) => event.stopPropagation()}
          style={{
            position: "absolute",
            left: "16px",
            bottom: isPhone ? "70px" : "58px",
            zIndex: 61,
            display: "flex",
            alignItems: "center",
            gap: "5px",
            padding: "4px",
            borderRadius: "9px",
            background: "rgba(255,255,255,0.94)",
            boxShadow: "0 2px 9px rgba(0,0,0,0.13)",
            backdropFilter: "blur(8px)",
            fontFamily: "Clash Grotesk, sans-serif",
          }}
        >
          <button
            type="button"
            onClick={onToggleReferenceArtwork}
            title={referenceArtworkVisible ? "Hide reference artwork" : "Show reference artwork"}
            aria-pressed={referenceArtworkVisible}
            style={{
              height: "28px", border: "none", borderRadius: "6px", cursor: "pointer",
              padding: "0 8px", display: "flex", alignItems: "center", gap: "5px",
              background: referenceArtworkVisible ? "rgba(124,58,237,0.10)" : "transparent",
              color: referenceArtworkVisible ? "#6d28d9" : "#777", fontSize: "11px", fontWeight: 700,
            }}
          >
            <span aria-hidden="true">{referenceArtworkVisible ? "◉" : "○"}</span>
            Artwork
          </button>
          <button
            type="button"
            onClick={() => setArtworkControlsOpen((open) => !open)}
            title="Reference artwork settings"
            aria-expanded={artworkControlsOpen}
            style={{ width: "28px", height: "28px", border: "none", borderRadius: "6px", background: "transparent", cursor: "pointer", color: "#777" }}
          >
            {artworkControlsOpen ? "‹" : "›"}
          </button>
          <button
            type="button"
            onClick={onRetraceArtwork}
            title="Retrace the reference artwork"
            style={{
              height: "28px", border: "1px solid rgba(124,58,237,0.20)", borderRadius: "6px",
              background: "rgba(124,58,237,0.08)", color: "#6d28d9", cursor: "pointer",
              padding: "0 9px", fontSize: "11px", fontWeight: 700,
            }}
          >
            Retrace
          </button>
          {artworkControlsOpen && (
            <>
              <span title="Reference artwork is locked" style={{ fontSize: "11px", color: "#777", whiteSpace: "nowrap" }}>🔒 Locked</span>
              <input
                aria-label="Reference artwork opacity"
                title={`Artwork opacity ${Math.round(referenceArtworkOpacity * 100)}%`}
                type="range"
                min="0.08"
                max="1"
                step="0.01"
                value={referenceArtworkOpacity}
                disabled={!referenceArtworkVisible}
                onChange={(event) => onReferenceArtworkOpacityChange?.(Number(event.target.value))}
                style={{ width: "82px", accentColor: "#7c3aed" }}
              />
              <span style={{ width: "30px", fontSize: "10px", color: "#777" }}>{Math.round(referenceArtworkOpacity * 100)}%</span>
            </>
          )}
        </div>
      )}

      {/* Zoom controls — bottom-left on desktop / top-right on phone, matching
          the SVG editor's mobile EditorFooter placement so the bottom-anchored
          stitch toolbar doesn't overlap them. */}
      <div
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          ...(isPhone
            ? { top: "8px", right: "8px", bottom: "auto", left: "auto" }
            : { bottom: "16px", left: "16px" }),
          display: "flex",
          alignItems: "center",
          gap: "2px",
          background: "rgba(255,255,255,0.92)",
          borderRadius: "8px",
          padding: "3px 4px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          backdropFilter: "blur(8px)",
          zIndex: 60,
        }}
      >
        <button
          onClick={() => zoomTowardCenter(1 / 1.3)}
          title="Zoom out"
          style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: "16px", fontWeight: 700, width: "28px", height: "28px", borderRadius: "5px", color: "#555", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
          onMouseEnter={(e) => e.currentTarget.style.background = "#f0f0f0"}
          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
        >−</button>
        <span style={{ fontSize: "11px", fontWeight: 600, color: "#666", minWidth: "38px", textAlign: "center", fontFamily: "Clash Grotesk, sans-serif", userSelect: "none" }}>
          {Math.round(zoom * 100)}%
        </span>
        <button
          onClick={() => zoomTowardCenter(1.3)}
          title="Zoom in"
          style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: "16px", fontWeight: 700, width: "28px", height: "28px", borderRadius: "5px", color: "#555", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
          onMouseEnter={(e) => e.currentTarget.style.background = "#f0f0f0"}
          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
        >+</button>
        <div style={{ width: "1px", height: "18px", background: "#ddd", margin: "0 2px" }} />
        <button
          onClick={() => {
            // Reset pan/zoom so the design is centered in the current viewport
            const container = containerRef.current;
            const cw = container ? container.offsetWidth : BASE_W;
            const ch = container ? container.offsetHeight : BASE_H;
            const newPan = { x: (cw - BASE_W) / 2, y: (ch - BASE_H) / 2 };
            panRef.current = newPan;
            setPan(newPan);
            setZoom(1);
          }}
          title="Reset view"
          style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: "13px", width: "28px", height: "28px", borderRadius: "5px", color: "#555", display: "flex", alignItems: "center", justifyContent: "center" }}
          onMouseEnter={(e) => e.currentTarget.style.background = "#f0f0f0"}
          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
        >
          {/* Rotate-left icon (matching SVG editor Reset View) */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
        </button>
        {spacePressed && (
          <div style={{ fontSize: "10px", color: "#888", padding: "0 4px", fontFamily: "Clash Grotesk, sans-serif", fontWeight: 600 }}>
            Pan
          </div>
        )}
      </div>

      {/* AI analyzing overlay — shown while AI is still loading */}
      {aiLoading && !isConverting && (
        <div data-tutorial-busy="analyzing-design" style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(226,216,200,0.7)",
          backdropFilter: "blur(4px)",
          zIndex: 9,
        }}>
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
            background: "rgba(255,255,255,0.95)",
            padding: "16px 24px",
            borderRadius: "12px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            fontFamily: "Clash Grotesk, sans-serif",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{
                width: "16px", height: "16px",
                border: "2px solid #a855f7", borderTopColor: "transparent",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }} />
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#555" }}>
                AI analyzing your design…
              </span>
            </div>
            <span style={{ fontSize: "10px", color: "#999", fontWeight: 500 }}>
              Optimizing stitch settings for each layer
            </span>
          </div>
        </div>
      )}

      {/* Converting overlay */}
      {isConverting && (
        <div style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(226,216,200,0.7)",
          backdropFilter: "blur(4px)",
          zIndex: 10,
        }}>
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
            background: "rgba(255,255,255,0.95)",
            padding: "16px 24px",
            borderRadius: "12px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            fontFamily: "Clash Grotesk, sans-serif",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{
                width: "16px", height: "16px",
                border: "2px solid #d946ef", borderTopColor: "transparent",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }} />
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#555" }}>
                {convertStatus || "Generating stitch preview..."}
              </span>
            </div>
            <span style={{ fontSize: "10px", color: "#999", fontWeight: 500 }}>
              {elapsed > 0
                ? `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")} elapsed — multi-layer designs can take up to a minute`
                : "First conversion may take longer while the server warms up"}
            </span>
            {onCancelConvert && (
              <button
                onClick={onCancelConvert}
                style={{
                  marginTop: "4px",
                  padding: "5px 16px",
                  fontSize: "11px",
                  fontWeight: 600,
                  fontFamily: "Clash Grotesk, sans-serif",
                  color: "#888",
                  background: "transparent",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* Pixel-erase "Cut" overlay */}
      {eraseActive && (
        <StitchEraseCanvas
          key={`cut-${eraseLayerIdx ?? currentLayerIdx}`}
          ref={cutToolRef}
          svgJoined={svgJoined}
          layerIdx={eraseLayerIdx ?? currentLayerIdx}
          colors={colors}
          layoutMetrics={layoutMetrics}
          pan={pan}
          zoom={zoom}
          initialCutMarks={layerCutMarks?.[eraseLayerIdx ?? currentLayerIdx] || []}
          onCutMarksChange={(marks) => onCutMarksChange?.(eraseLayerIdx ?? currentLayerIdx, marks)}
          onComplete={onEraseComplete}
          onCancel={onEraseCancel}
          onDraftHistoryChange={onEraseDraftHistoryChange}
        />
      )}

      {/* Paint-bucket "Fill" overlay */}
      {fillActive && (
        <StitchFillCanvas
          key={`fill-${fillLayerIdx ?? currentLayerIdx}`}
          ref={fillToolRef}
          svgJoined={svgJoined}
          svgSplit={svgSplit}
          layerIdx={fillLayerIdx ?? currentLayerIdx}
          colors={colors}
          layoutMetrics={layoutMetrics}
          pan={pan}
          zoom={zoom}
          onApply={onFillApply}
        />
      )}

      {/* Pixel-by-pixel brush eraser overlay */}
      {pixelEraseActive && (
        <StitchPixelEraseCanvas
          key={`pixel-erase-${pixelEraseLayerIdx ?? currentLayerIdx}`}
          ref={pixelEraseToolRef}
          svgJoined={svgJoined}
          svgSplit={svgSplit}
          layerIdx={pixelEraseLayerIdx ?? currentLayerIdx}
          colors={colors}
          layoutMetrics={layoutMetrics}
          pan={pan}
          zoom={zoom}
          onApply={onPixelEraseApply}
        />
      )}

      {/* Pixel-by-pixel brush draw overlay (additive twin of the eraser) */}
      {drawActive && (
        <StitchDrawCanvas
          key={`draw-${drawLayerIdx ?? currentLayerIdx}`}
          ref={drawToolRef}
          svgJoined={svgJoined}
          svgSplit={svgSplit}
          layerIdx={drawLayerIdx ?? currentLayerIdx}
          colors={colors}
          layoutMetrics={layoutMetrics}
          pan={pan}
          zoom={zoom}
          onApply={onDrawApply}
        />
      )}

      {/* Expand/Shrink (dilate/erode) overlay — slider-driven layer offset */}
      {morphActive && (
        <StitchMorphCanvas
          key={`morph-${morphLayerIdx ?? currentLayerIdx}`}
          ref={morphToolRef}
          svgJoined={svgJoined}
          svgSplit={svgSplit}
          layerIdx={morphLayerIdx ?? currentLayerIdx}
          colors={colors}
          layoutMetrics={layoutMetrics}
          pan={pan}
          zoom={zoom}
          onApply={onMorphApply}
        />
      )}

      {/* Floating "+ Guides" buttons near each complete rail pair — only while
          editing stitch (Stitch panel open), like the rail overlay. */}
      {hasEditableLayer && satinEditingActive && !railEditing && !activeRailDrawing && !activeRungDrawing &&
        (currentRailPairs || [])
          .map((pair, pairIdx) => {
            if (!pair.rail0 || !pair.rail1) return null;
            // rail0/rail1 may be polylines ("M x,y L x,y ...") or curve paths
            // ("M x,y C ...") for auto-detected pairs. Use getPointAtLength when
            // the path has curves so the button anchors to the true midpoint.
            const parsePathMidpoint = (d) => {
              if (/[CSQTAcsqta]/.test(d)) {
                try {
                  const samples = samplePathPoints(d, 20);
                  return samples[Math.floor(samples.length / 2)] || samples[0];
                } catch (_) { /* fall through to regex */ }
              }
              const pts = [];
              const re = /[ML]\s*([\d.eE+-]+)[,\s]+([\d.eE+-]+)/gi;
              let m;
              while ((m = re.exec(d)) !== null) pts.push({ x: parseFloat(m[1]), y: parseFloat(m[2]) });
              return pts[Math.floor(pts.length / 2)] || pts[0];
            };
            const mid0 = parsePathMidpoint(pair.rail0);
            const mid1 = parsePathMidpoint(pair.rail1);
            if (!mid0 || !mid1) return null;
            const vbMidX = (mid0.x + mid1.x) / 2;
            const vbMidY = (mid0.y + mid1.y) / 2;
            // Convert viewBox → canvas pixel coords
            const { vbX: lvbX, vbY: lvbY, scaleX, scaleY, designLeft, designTop } = layoutMetrics;
            const canvasX = (vbMidX - lvbX) * scaleX + designLeft;
            const canvasY = (vbMidY - lvbY) * scaleY + designTop;
            // Apply current pan/zoom to get screen coords
            const screenX = canvasX * zoom + pan.x;
            const screenY = canvasY * zoom + pan.y;
            const pairRungCount = pair.rungs?.length || 0;
            const RUNG_COLORS = ["#22c55e", "#f97316", "#a855f7", "#f43f5e", "#06b6d4", "#f59e0b"];
            const color = RUNG_COLORS[pairIdx % RUNG_COLORS.length];
            const multiPair = (currentRailPairs || []).filter(p => p.rail0 && p.rail1).length > 1;
            const expanded = hoveredGuideIdx === pairIdx;
            return (
              <div
                key={`guide-btn-${pairIdx}`}
                // Hovering this is what opens the pair's controls, so a
                // walkthrough needs to be able to name it and open them itself.
                data-tutorial="satin-guide-controls"
                data-guide-pair={pairIdx}
                data-pan-zoom="point"
                data-canvas-x={canvasX}
                data-canvas-y={canvasY}
                onMouseEnter={() => setHoveredGuideIdx(pairIdx)}
                onMouseLeave={() => setHoveredGuideIdx((cur) => (cur === pairIdx ? null : cur))}
                style={{
                  position: "absolute",
                  left: `${screenX}px`,
                  top: `${screenY}px`,
                  transform: "translate(-50%, -50%)",
                  zIndex: expanded ? 40 : 30,
                  display: "flex", alignItems: "center", gap: "4px",
                  pointerEvents: "auto",
                }}
              >
                {!expanded ? (
                  // Collapsed: a small dot. Number when there are multiple pairs,
                  // otherwise a "+". A tiny badge shows the guide count if any.
                  <button
                    // Same anchor as the expanded button below: this dot is what
                    // is on screen until the pointer arrives, so it's what a
                    // walkthrough has to be able to point at.
                    data-tutorial="satin-add-guides"
                    data-guide-pair={pairIdx}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); onStartRungDrawing?.(currentLayerIdx, pairIdx); }}
                    title={multiPair ? `Edge pair ${pairIdx + 1} — add guides` : "Add guides"}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center",
                      width: "18px", height: "18px", padding: 0,
                      background: pairRungCount > 0 ? color : "rgba(255,255,255,0.9)",
                      border: `1.5px solid ${color}`,
                      borderRadius: "50%",
                      cursor: "pointer",
                      fontSize: "10px", fontWeight: 800,
                      color: pairRungCount > 0 ? "#fff" : color,
                      fontFamily: "Clash Grotesk, sans-serif",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                      lineHeight: 1,
                      transition: "transform 0.12s ease",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.15)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = ""; }}
                  >
                    {multiPair ? pairIdx + 1 : "+"}
                  </button>
                ) : (
                  <>
                    <button
                      data-tutorial="satin-add-guides"
                      data-guide-pair={pairIdx}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        onStartRungDrawing?.(currentLayerIdx, pairIdx);
                      }}
                      title="Draw guide lines across this edge pair to steer the satin stitches"
                      style={{
                        display: "flex", alignItems: "center", gap: "5px",
                        padding: "5px 11px",
                        background: `linear-gradient(135deg, ${color}, ${color}cc)`,
                        border: "none",
                        borderRadius: "20px",
                        cursor: "pointer",
                        fontSize: "11px", fontWeight: 700,
                        color: "#fff",
                        fontFamily: "Clash Grotesk, sans-serif",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                        whiteSpace: "nowrap",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.filter = "brightness(1.08)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.filter = ""; }}
                    >
                      <span style={{ fontSize: "13px", lineHeight: 1 }}>✎</span>
                      {pairRungCount > 0 ? "Add more guides" : "Add guides"}
                      {multiPair && (
                        <span style={{
                          background: "rgba(255,255,255,0.25)", borderRadius: "8px",
                          padding: "0 5px", fontSize: "10px", fontWeight: 700,
                        }}>
                          #{pairIdx + 1}
                        </span>
                      )}
                      {pairRungCount > 0 && (
                        <span style={{
                          background: "rgba(255,255,255,0.3)", borderRadius: "10px",
                          padding: "1px 5px", fontSize: "10px", fontWeight: 700,
                        }}>
                          {pairRungCount}
                        </span>
                      )}
                    </button>
                    <button
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); onStartRailEditing?.(currentLayerIdx, pairIdx, 0); }}
                      title="Edit rail points"
                      style={{ padding: "5px 11px", borderRadius: "20px", border: "1px solid #c4b5fd", background: "#fff", color: "#6d28d9", fontSize: "11px", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
                    >Edit rails</button>
                    {pairRungCount > 0 && (
                      <button
                        data-tutorial="satin-clear-guides"
                        data-guide-pair={pairIdx}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          onClearRungs?.(currentLayerIdx, pairIdx);
                        }}
                        title="Erase the guide lines on this pair (keeps the satin edges)"
                        style={{
                          display: "flex", alignItems: "center", gap: "4px",
                          padding: "5px 10px",
                          background: "rgba(255,255,255,0.95)",
                          border: "1.5px solid rgba(0,0,0,0.12)",
                          borderRadius: "20px",
                          cursor: "pointer",
                          fontSize: "11px", fontWeight: 600, color: "#92660a",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                          backdropFilter: "blur(6px)",
                          whiteSpace: "nowrap",
                          transition: "all 0.12s ease",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "#fef3c7"; e.currentTarget.style.borderColor = "#fcd34d"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.95)"; e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)"; }}
                      >
                        <span style={{ fontSize: "12px", lineHeight: 1 }}>⌫</span>
                        Clear guides
                      </button>
                    )}
                    <button
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveRailPair?.(currentLayerIdx, pairIdx);
                      }}
                      title="Delete this satin edge pair entirely"
                      style={{
                        display: "flex", alignItems: "center", gap: "4px",
                        padding: "5px 10px",
                        background: "rgba(255,255,255,0.95)",
                        border: "1.5px solid rgba(0,0,0,0.12)",
                        borderRadius: "20px",
                        cursor: "pointer",
                        fontSize: "11px", fontWeight: 600, color: "#b91c1c",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        backdropFilter: "blur(6px)",
                        whiteSpace: "nowrap",
                        transition: "all 0.12s ease",
                        lineHeight: 1,
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#fee2e2"; e.currentTarget.style.borderColor = "#fca5a5"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.95)"; e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)"; }}
                    >
                      <span style={{ fontSize: "12px", lineHeight: 1 }}>🗑</span>
                      Remove edges
                    </button>
                  </>
                )}
              </div>
            );
          })
      }

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse-dot { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
        @keyframes sew-in-reveal {
          0% { clip-path: inset(0 100% 0 0); opacity: 0.3; }
          40% { opacity: 1; }
          100% { clip-path: inset(0 0 0 0); opacity: 1; }
        }
        .sew-in-active {
          animation: sew-in-reveal 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }
      `}</style>
    </div>
  );
};

export default StitchCanvas;
